import React, { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import {Plus, Trash2, GripVertical, Type, SquareCheck as CheckSquare, Circle, Square, List, Grid3X3, Image, Star, Sliders, BarChart3, TrendingUp, FileText, Hash, Calendar, Mail, Settings, Eye, Copy, ArrowUp, ArrowDown, MoveUp, MoveDown, GitBranch, Layers, Filter, Search, X, Upload, Loader2} from 'lucide-react'
import toast from 'react-hot-toast'
import RichTextEditor from './RichTextEditor'
import { lumi } from '../lib/lumi'
import { getImageDisplayHint } from '../lib/surveyPresentation'

export interface QuestionOption {
  id: string
  text: string
  value?: string | number
  image?: string
  translations?: Partial<Record<SurveyLanguage, string>>
  isExclusive?: boolean
  displayConditions?: DisplayCondition[]
  displayLogic?: 'AND' | 'OR'
}

export interface QuestionValidation {
  required: boolean
  minLength?: number
  maxLength?: number
  minValue?: number
  maxValue?: number
  minSelections?: number
  maxSelections?: number
  pattern?: string
  customMessage?: string
}

export type SurveyLanguage = 'pt' | 'en' | 'es'

export interface QuestionTranslationContent {
  title?: string
  description?: string
}

export interface MatrixRow {
  id: string
  label: string
  displayConditions?: DisplayCondition[]
  displayLogic?: 'AND' | 'OR'
}

export interface MatrixColumn {
  id: string
  label: string
  value: string | number
  displayConditions?: DisplayCondition[]
  displayLogic?: 'AND' | 'OR'
}

export interface MultiTextBox {
  id: string
  label: string
  type: 'text' | 'number'
  placeholder?: string
  required?: boolean
  displayConditions?: DisplayCondition[]
  displayLogic?: 'AND' | 'OR'
}

export interface ConditionalBranch {
  id: string
  optionId?: string // ID da opção que dispara o desvio (legado)
  targetPageId?: string // ID da página de destino (legado)
  targetQuestionId?: string
  targetType?: 'question' | 'page' | 'complete' | 'cancel_filter'
  condition?: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_answered' | 'not_answered' | 'greater_than' | 'less_than' | 'greater_or_equal' | 'less_or_equal'
  operator?: string
  value?: string
  answerValue?: string
  conditions?: DisplayCondition[]
  logic?: 'AND' | 'OR'
  answerPath?: string
}

export interface DisplayCondition {
  id: string
  questionId: string // ID da pergunta que deve ser respondida
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'greater_or_equal' | 'less_or_equal' | 'is_answered' | 'not_answered'
  value?: any // valor esperado (para comparaÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes)
  optionId?: string // ID da opÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o selecionada (para perguntas de escolha)
  answerPath?: string
  logicalOperator?: 'AND' | 'OR' | 'NOT' // operador lÃƒÆ’Ã‚Â³gico com a prÃƒÆ’Ã‚Â³xima condiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o
}

export interface SurveyPage {
  id: string
  title: string
  description?: string
  questionIds: string[] // IDs das perguntas nesta pÃƒÆ’Ã‚Â¡gina
}

export interface SurveyPage {
  showTitle?: boolean
  terminalOutcome?: 'completed' | 'cancel_filter'
  displayConditions?: DisplayCondition[]
  displayLogic?: 'AND' | 'OR'
  conditionalBranches?: ConditionalBranch[]
}

export interface AnswerPipingConfig {
  titleTemplate?: string
  descriptionTemplate?: string
}

export interface OptionTransferConfig {
  sourceQuestionId?: string
  mode: 'include_selected' | 'exclude_selected'
  logic?: 'AND' | 'OR'
  rules?: Array<{
    sourceQuestionId: string
    mode: 'include_selected' | 'exclude_selected'
  }>
}

export interface Question {
  id: string
  type: QuestionType
  title: string
  /** Código curto administrativo usado em condições, desvios e exportações. */
  variable?: string
  description?: string
  required: boolean
  options?: QuestionOption[]
  validation?: QuestionValidation
  pageId?: string // ID da pÃƒÆ’Ã‚Â¡gina ÃƒÆ’Ã‚Â  qual esta pergunta pertence
  
  // ConfiguraÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes especÃƒÆ’Ã‚Â­ficas por tipo
  allowOther?: boolean
  otherLabel?: string
  otherPlaceholder?: string
  randomizeOptions?: boolean
  randomizeFixedLastCount?: number
  sortMatrixRowsAlphabetically?: boolean
  multipleColumns?: boolean
  
  // Para matriz
  matrixRows?: MatrixRow[]
  matrixColumns?: MatrixColumn[]
  
  // Para escala/rating
  minValue?: number
  maxValue?: number
  minLabel?: string
  maxLabel?: string
  stepValue?: number
  
  // Para vÃƒÆ’Ã‚Â¡rias caixas de texto
  textBoxes?: MultiTextBox[]
  sumValidation?: {
    enabled: boolean
    targetSum: number
    errorMessage: string
  }
  
  // Para data/hora
  dateFormat?: 'date' | 'time' | 'datetime'
  
  // ConfiguraÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes visuais
  imageUrl?: string
  videoUrl?: string
  showProgress?: boolean
  imageDisplaySize?: 'small' | 'medium' | 'large' | 'original'
  translations?: Partial<Record<SurveyLanguage, QuestionTranslationContent>>
  
  // Desvios condicionais
  conditionalBranches?: ConditionalBranch[]
  
  // CondiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes de exibiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o
  displayConditions?: DisplayCondition[]
  displayLogic?: 'AND' | 'OR' // Como combinar mÃƒÆ’Ã‚ºltiplas condiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes (padrÃƒÆ’Ã‚Â£o: AND)
}

export interface Question {
  requireOtherText?: boolean
  trackSelectionOrder?: boolean
  ratingIcon?: 'star' | 'heart' | 'thumb' | 'smile'
  ratingColor?: string
  ratingSize?: 'small' | 'medium' | 'large'
  answerPiping?: AnswerPipingConfig
  textTransfer?: AnswerPipingConfig
  optionTransfer?: OptionTransferConfig
}

export type QuestionType = 
  | 'text'           // Texto Livre
  | 'multiple'       // MÃƒÆ’Ã‚ºltipla escolha
  | 'single'         // Fechada ÃƒÆ’Ã‚ºnica
  | 'checkbox'       // Fechada mÃƒÆ’Ã‚ºltipla
  | 'ranking'        // Fechada mÃƒÆ’Ã‚ºltipla ordenada
  | 'dropdown'       // Lista suspensa
  | 'dropdown_matrix'
  | 'matrix'         // Matriz de menus suspensos
  | 'media'          // OpÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o de imagem/vÃƒÆ’Ã‚Â­deo
  | 'rating'         // AvaliaÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o com estrelas
  | 'rating_matrix'
  | 'slider'         // Barra deslizante
  | 'slider_matrix'
  | 'classification' // ClassificaÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o
  | 'bestworst'      // Escala Melhor/Pior
  | 'opentext'       // Aberta texto
  | 'number'         // Aberta nÃƒÆ’Ã‚ºmero
  | 'multitext'      // VÃƒÆ’Ã‚Â¡rias caixas de texto
  | 'datetime'       // Data/Hora
  | 'email'          // E-mail

const questionTypes = [
  { type: 'text' as QuestionType, label: 'Texto Livre', icon: Type, description: 'Resposta aberta em texto corrido.' },
  { type: 'multiple' as QuestionType, label: 'Escolha Múltipla (legado)', icon: CheckSquare, description: 'Alias legado mantido para pesquisas antigas.' },
  { type: 'single' as QuestionType, label: 'Escolha Única', icon: Circle, description: 'Uma única opção deve ser escolhida.' },
  { type: 'checkbox' as QuestionType, label: 'Escolha Múltipla', icon: Square, description: 'Mais de uma opção pode ser marcada.' },
  { type: 'ranking' as QuestionType, label: 'Ranking Ordenado', icon: List, description: 'Ordene TODAS as opções por preferência (1º ao último).' },
  { type: 'classification' as QuestionType, label: 'Resposta Ordenada', icon: BarChart3, description: 'Clique nas opções; a ordem do clique define a posição (1, 2, 3...).' },
  { type: 'dropdown' as QuestionType, label: 'Lista Suspensa', icon: List, description: 'Seleciona uma opção dentro de um menu.' },
  { type: 'dropdown_matrix' as QuestionType, label: 'Lista Suspensa Combinada', icon: Grid3X3, description: 'Avalia vários itens usando a mesma lista suspensa.' },
  { type: 'matrix' as QuestionType, label: 'Matriz', icon: Grid3X3, description: 'Tabela com linhas e colunas configuraveis.' },
  { type: 'media' as QuestionType, label: 'Midia', icon: Image, description: 'Pergunta com apoio visual de imagem ou video.' },
  { type: 'rating' as QuestionType, label: 'Avaliação por Estrelas', icon: Star, description: 'Escala curta de avaliação.' },
  { type: 'rating_matrix' as QuestionType, label: 'Estrelas Combinada', icon: Star, description: 'Avalia vários itens com a mesma escala visual.' },
  { type: 'slider' as QuestionType, label: 'Escala Deslizante', icon: Sliders, description: 'Controle continuo entre mínimo e máximo.' },
  { type: 'slider_matrix' as QuestionType, label: 'Escala Deslizante Combinada', icon: Sliders, description: 'Avalia vários itens com a mesma barra deslizante.' },
  { type: 'bestworst' as QuestionType, label: 'Melhor x Pior', icon: TrendingUp, description: 'Comparação entre extremos de preferencia.' },
  { type: 'opentext' as QuestionType, label: 'Texto Livre (legado)', icon: FileText, description: 'Alias legado mantido para pesquisas antigas.' },
  { type: 'number' as QuestionType, label: 'Aberta Número', icon: Hash, description: 'Campo aberto restrito a números.' },
  { type: 'multitext' as QuestionType, label: 'Multiplos Campos', icon: Grid3X3, description: 'Conjunto de campos independentes.' },
  { type: 'datetime' as QuestionType, label: 'Data e Hora', icon: Calendar, description: 'Seleciona data, hora ou ambos.' },
  { type: 'email' as QuestionType, label: 'E-mail', icon: Mail, description: 'Campo com validação de e-mail.' }
]

const visibleQuestionTypes = questionTypes.filter((item) => !['multiple', 'opentext'].includes(item.type))

interface BankQuestion {
  _id: string
  type: string
  translations: {
    pt: {
      title: string
      description?: string
      options?: string[]
    }
    en?: {
      title?: string
      description?: string
      options?: string[]
    }
    es?: {
      title?: string
      description?: string
      options?: string[]
    }
  }
  isRequired: boolean
  category?: string
  settings?: Record<string, any>
  mediaUrl?: string
}

const isQuestionType = (value: unknown): value is QuestionType =>
  typeof value === 'string' && questionTypes.some((item) => item.type === value)

const mapBankQuestionType = (type: string): QuestionType => {
  const typeMap: Record<string, QuestionType> = {
    multiple_choice: 'single',
    single_choice: 'single',
    multiple_select: 'checkbox',
    ordered_multiple: 'ranking',
    dropdown: 'dropdown',
    matrix_dropdown: 'dropdown_matrix',
    dropdown_matrix: 'dropdown_matrix',
    media: 'media',
    star_rating: 'rating',
    rating_matrix: 'rating_matrix',
    slider: 'slider',
    slider_matrix: 'slider_matrix',
    ranking: 'ranking',
    classification: 'classification',
    best_worst: 'bestworst',
    open_text: 'text',
    open_number: 'number',
    multiple_textboxes: 'multitext',
    datetime: 'datetime',
    email: 'email'
  }

  return typeMap[type] || 'text'
}

const stripHtml = (html: string) => {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

export const getQuestionReferenceLabel = (question?: Pick<Question, 'title' | 'variable'>) => {
  if (!question) return 'Pergunta não encontrada'
  const variable = question.variable?.trim()
  const title = stripHtml(question.title || '').trim() || 'Pergunta sem título'
  return variable ? `${variable} — ${title}` : title
}

const getConditionOperatorLabel = (operator: DisplayCondition['operator']) => {
  switch (operator) {
    case 'equals':
      return 'igual a'
    case 'not_equals':
      return 'diferente de'
    case 'contains':
      return 'contem'
    case 'greater_than':
      return 'maior que'
    case 'less_than':
      return 'menor que'
    case 'is_answered':
      return 'foi respondida'
    case 'not_answered':
      return 'não foi respondida'
    default:
      return operator
  }
}

const getQuestionConditionSummaries = (question: Question, questions: Question[]) => (
  question.displayConditions || []
).map((condition) => {
  const sourceQuestion = questions.find((item) => item.id === condition.questionId)
  const sourceTitle = stripHtml(sourceQuestion?.title || 'Pergunta')
  const sourceOption = sourceQuestion?.options?.find((item) => item.id === condition.optionId)
  const valueLabel = sourceOption
    ? stripHtml(sourceOption.text)
    : stripHtml(String(condition.value ?? ''))

  if (condition.operator === 'is_answered' || condition.operator === 'not_answered') {
    return `${sourceTitle} ${getConditionOperatorLabel(condition.operator)}`
  }

  return `${sourceTitle} ${getConditionOperatorLabel(condition.operator)} ${valueLabel}`.trim()
})

const getQuestionBranchSummaries = (question: Question, pages: SurveyPage[]) => (
  question.conditionalBranches || []
).map((branch) => {
  const optionText = stripHtml(question.options?.find((item) => item.id === branch.optionId)?.text || '')
  const pageTitle = stripHtml(pages.find((item) => item.id === branch.targetPageId)?.title || '')
  const prefix = branch.condition === 'equals'
    ? 'Se responder'
    : branch.condition === 'not_equals'
      ? 'Se não responder'
      : 'Se contiver'

  return `${prefix} ${optionText}, ir para ${pageTitle}`.trim()
})

const buildQuestionPageMap = (pages: SurveyPage[]) => {
  const pageMap = new Map<string, string>()

  pages.forEach((page) => {
    page.questionIds.forEach((questionId) => {
      if (!pageMap.has(questionId)) {
        pageMap.set(questionId, page.id)
      }
    })
  })

  return pageMap
}

export const buildOrderedQuestions = (
  questions: Question[],
  pages: SurveyPage[]
): Question[] => {
  const questionMap = new Map<string, Question>()
  questions.forEach((question) => questionMap.set(question.id, question))

  const orderedQuestions: Question[] = []
  const addedQuestionIds = new Set<string>()

  pages.forEach((page) => {
    page.questionIds.forEach((questionId) => {
      const question = questionMap.get(questionId)

      if (question && !addedQuestionIds.has(questionId)) {
        orderedQuestions.push(question)
        addedQuestionIds.add(questionId)
      }
    })
  })

  questions.forEach((question) => {
    if (!addedQuestionIds.has(question.id)) {
      orderedQuestions.push(question)
      addedQuestionIds.add(question.id)
    }
  })

  return orderedQuestions
}

export const normalizeQuestionPageState = (
  rawQuestions: Question[],
  rawPages: SurveyPage[]
): { questions: Question[]; pages: SurveyPage[] } => {
  const questions = rawQuestions.map((question) => ({ ...question }))

  if (questions.length === 0) {
    return {
      questions,
      pages: rawPages.map((page, index) => ({
        ...page,
        title: page.title || `Página ${index + 1}`,
        description: page.description || '',
        questionIds: Array.from(new Set(page.questionIds || []))
      }))
    }
  }

  const questionIds = new Set(questions.map((question) => question.id))
  let pages = rawPages.map((page, index) => ({
    ...page,
    title: page.title || `Página ${index + 1}`,
    description: page.description || '',
    questionIds: Array.from(
      new Set((page.questionIds || []).filter((questionId) => questionIds.has(questionId)))
    )
  }))

  if (pages.length === 0) {
    pages = [
      {
        id: `page_${questions[0].id}`,
        title: 'Página 1',
        description: '',
        questionIds: questions.map((question) => question.id)
      }
    ]
  } else {
    const assignedQuestionIds = new Set<string>()

    pages = pages.map((page) => ({
      ...page,
      questionIds: page.questionIds.filter((questionId) => {
        if (assignedQuestionIds.has(questionId)) {
          return false
        }

        assignedQuestionIds.add(questionId)
        return true
      })
    }))

    const orphanQuestionIds = questions
      .map((question) => question.id)
      .filter((questionId) => !assignedQuestionIds.has(questionId))

    if (orphanQuestionIds.length > 0) {
      pages = pages.map((page, index) => (
        index === 0
          ? { ...page, questionIds: [...page.questionIds, ...orphanQuestionIds] }
          : page
      ))
    }
  }

  const pageMap = buildQuestionPageMap(pages)
  const normalizedQuestions = buildOrderedQuestions(questions, pages).map((question) => {
    const pageId = pageMap.get(question.id)

    if (pageId && question.pageId !== pageId) {
      return { ...question, pageId }
    }

    if (!pageId && question.pageId) {
      return { ...question, pageId: undefined }
    }

    return question
  })

  return {
    questions: normalizedQuestions,
    pages
  }
}

const questionTypesWithConfigurableInitialOptions: QuestionType[] = [
  'multiple',
  'single',
  'checkbox',
  'dropdown',
  'dropdown_matrix',
  'ranking',
  'classification',
  'bestworst'
]

const supportsInitialOptionCount = (type: QuestionType) => (
  questionTypesWithConfigurableInitialOptions.includes(type)
)

const supportsSelectionLimits = (type: QuestionType) => (
  type === 'multiple' || type === 'checkbox' || type === 'classification'
)

export const getQuestionAnswerPathOptions = (question?: Question | null) => {
  if (!question) return []

  if (question.type === 'multitext') {
    return (question.textBoxes || []).map((box, index) => ({
      value: box.id,
      label: box.label || `Campo ${index + 1}`
    }))
  }

  if (['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(question.type)) {
    return (question.matrixRows || []).map((row, index) => ({
      value: row.id,
      label: row.label || `Item ${index + 1}`
    }))
  }

  return []
}

export interface QuestionAnswerValueOption {
  value: string
  label: string
  storeAs: 'optionId' | 'value'
}

export const getQuestionAnswerValueOptions = (
  question?: Question | null,
  answerPath?: string
): QuestionAnswerValueOption[] => {
  if (!question) return []

  if ((question.options?.length || 0) > 0 && !answerPath) {
    return (question.options || []).map((option) => ({
      value: String(option.id),
      label: stripHtml(option.text || ''),
      storeAs: 'optionId' as const
    }))
  }

  if (['matrix', 'dropdown_matrix'].includes(question.type) && answerPath) {
    return (question.matrixColumns || []).map((column, index) => ({
      value: String(column.value ?? column.id),
      label: column.label || `Opção ${index + 1}`,
      storeAs: 'value' as const
    }))
  }

  return []
}

const normalizeInitialOptionCount = (count?: number) => {
  if (typeof count !== 'number' || !Number.isFinite(count)) {
    return 2
  }

  return Math.min(20, Math.max(2, Math.floor(count)))
}

const buildDefaultClosedQuestionOptions = (
  generateId: () => string,
  count?: number
): QuestionOption[] => Array.from({ length: normalizeInitialOptionCount(count) }, (_, index) => ({
  id: generateId(),
  text: `Opção ${index + 1}`
}))

const createQuestionByType = (
  type: QuestionType,
  generateId: () => string,
  overrides: Partial<Question> = {},
  initialOptionCount?: number
): Question => {
  const baseQuestion: Question = {
    id: overrides.id || generateId(),
    type,
    title: overrides.title || '',
    description: overrides.description || '',
    required: overrides.required || false,
    pageId: overrides.pageId,
    imageUrl: overrides.imageUrl,
    videoUrl: overrides.videoUrl,
    showProgress: overrides.showProgress,
    imageDisplaySize: overrides.imageDisplaySize || 'original',
    translations: overrides.translations,
    conditionalBranches: overrides.conditionalBranches,
    displayConditions: overrides.displayConditions,
    displayLogic: overrides.displayLogic
  }

  switch (type) {
    case 'multiple':
    case 'single':
    case 'checkbox':
    case 'dropdown':
    case 'ranking':
      return {
        ...baseQuestion,
        options: buildDefaultClosedQuestionOptions(generateId, initialOptionCount),
        allowOther: false,
        otherLabel: 'Outro',
        otherPlaceholder: 'Digite sua resposta',
        requireOtherText: true,
        trackSelectionOrder: false,
        randomizeOptions: false,
        randomizeFixedLastCount: 0
      }

    case 'classification':
      return {
        ...baseQuestion,
        options: buildDefaultClosedQuestionOptions(generateId, initialOptionCount),
        allowOther: false,
        otherLabel: 'Outro',
        otherPlaceholder: 'Digite sua resposta',
        requireOtherText: true,
        trackSelectionOrder: true,
        randomizeOptions: false,
        randomizeFixedLastCount: 0,
        // Nao gera niveis automaticos - resposta ordenada usa apenas options
        matrixColumns: undefined,
        matrixRows: undefined
      }

    case 'matrix':
    case 'dropdown_matrix':
      return {
        ...baseQuestion,
        matrixRows: [
          { id: generateId(), label: 'Linha 1' },
          { id: generateId(), label: 'Linha 2' }
        ],
        matrixColumns: [
          { id: generateId(), label: 'Coluna 1', value: '1' },
          { id: generateId(), label: 'Coluna 2', value: '2' }
        ]
      }

    case 'rating':
    case 'rating_matrix':
      return {
        ...baseQuestion,
        matrixRows: type === 'rating_matrix' ? [
          { id: generateId(), label: 'Item 1' },
          { id: generateId(), label: 'Item 2' }
        ] : undefined,
        minValue: 1,
        maxValue: 5,
        minLabel: 'Muito Ruim',
        maxLabel: 'Excelente',
        ratingIcon: 'star',
        ratingColor: '#facc15',
        ratingSize: 'medium'
      }

    case 'slider':
    case 'slider_matrix':
      return {
        ...baseQuestion,
        matrixRows: type === 'slider_matrix' ? [
          { id: generateId(), label: 'Item 1' },
          { id: generateId(), label: 'Item 2' }
        ] : undefined,
        minValue: 0,
        maxValue: 100,
        stepValue: 1,
        minLabel: 'Mínimo',
        maxLabel: 'Máximo'
      }

    case 'bestworst':
      return {
        ...baseQuestion,
        options: buildDefaultClosedQuestionOptions(generateId, initialOptionCount || 3)
      }

    case 'multitext':
      return {
        ...baseQuestion,
        textBoxes: [
          { id: generateId(), label: 'Campo 1', type: 'number', required: false },
          { id: generateId(), label: 'Campo 2', type: 'number', required: false }
        ],
        sumValidation: {
          enabled: false,
          targetSum: 100,
          errorMessage: 'A soma deve ser igual a 100'
        }
      }

    case 'datetime':
      return {
        ...baseQuestion,
        dateFormat: 'date'
      }

    case 'number':
      return {
        ...baseQuestion,
        validation: {
          required: false,
          minValue: undefined,
          maxValue: undefined
        }
      }

    case 'email':
    case 'text':
    case 'opentext':
      return {
        ...baseQuestion,
        validation: {
          required: false,
          minLength: undefined,
          maxLength: undefined
        }
      }

    default:
      return baseQuestion
  }
}

const convertQuestionType = (
  question: Question,
  nextType: QuestionType,
  generateId: () => string
): Question => createQuestionByType(nextType, generateId, {
  id: question.id,
  title: question.title,
  description: question.description,
  required: question.required,
  pageId: question.pageId,
  imageUrl: question.imageUrl,
  videoUrl: question.videoUrl,
  showProgress: question.showProgress,
  imageDisplaySize: question.imageDisplaySize,
  translations: question.translations,
  conditionalBranches: question.conditionalBranches,
  displayConditions: question.displayConditions,
  displayLogic: question.displayLogic
}, question.options?.length)

const cloneQuestionForDuplication = (
  question: Question,
  generateId: () => string
): Question => ({
  ...JSON.parse(JSON.stringify(question)),
  id: generateId(),
  title: `${question.title} (Copia)`,
  options: question.options?.map((option) => ({ ...option, id: generateId() })),
  matrixRows: question.matrixRows?.map((row) => ({ ...row, id: generateId() })),
  matrixColumns: question.matrixColumns?.map((column) => ({ ...column, id: generateId() })),
  textBoxes: question.textBoxes?.map((box) => ({ ...box, id: generateId() })),
  conditionalBranches: question.conditionalBranches?.map((branch) => ({ ...branch, id: generateId() })),
  displayConditions: question.displayConditions?.map((condition) => ({ ...condition, id: generateId() }))
})

const createQuestionFromBank = (
  bankQuestion: BankQuestion,
  generateId: () => string,
  targetPageId?: string
): Question => {
  const ptTranslation = bankQuestion.translations?.pt || { title: '', description: '', options: [] as string[] }
  const enTranslation = bankQuestion.translations?.en
  const esTranslation = bankQuestion.translations?.es
  const settings = bankQuestion.settings || {}
  const mappedType = isQuestionType(settings.internalType)
    ? settings.internalType
    : mapBankQuestionType(bankQuestion.type)
  const savedOptions = Array.isArray(settings.options) ? settings.options : undefined
  const options = savedOptions
    ? savedOptions.map((option: any, index: number) => ({
      id: String(option.id || generateId()),
      text: option.text || ptTranslation.options?.[index] || `Opção ${index + 1}`,
      value: option.value,
      image: option.image,
      translations: option.translations,
      isExclusive: option.isExclusive,
      displayConditions: option.displayConditions,
      displayLogic: option.displayLogic,
    }))
    : ptTranslation.options?.map((optionText, index) => ({
      id: generateId(),
      text: optionText,
      translations: {
        pt: optionText,
        en: enTranslation?.options?.[index] || optionText,
        es: esTranslation?.options?.[index] || optionText,
      }
    }))

  const baseQuestion: Question = {
    id: generateId(),
    type: mappedType,
    title: ptTranslation.title || '',
    description: ptTranslation.description || '',
    required: bankQuestion.isRequired,
    pageId: targetPageId,
    validation: settings.validation,
    allowOther: settings.allowOther,
    otherLabel: settings.otherLabel,
    otherPlaceholder: settings.otherPlaceholder,
    requireOtherText: settings.requireOtherText,
    trackSelectionOrder: settings.trackSelectionOrder,
    randomizeOptions: settings.randomizeOptions,
    randomizeFixedLastCount: settings.randomizeFixedLastCount,
    multipleColumns: settings.multipleColumns,
    matrixRows: settings.matrixRows,
    matrixColumns: settings.matrixColumns,
    minValue: settings.minValue,
    maxValue: settings.maxValue,
    minLabel: settings.minLabel,
    maxLabel: settings.maxLabel,
    stepValue: settings.stepValue,
    ratingIcon: settings.ratingIcon,
    ratingColor: settings.ratingColor,
    ratingSize: settings.ratingSize,
    textBoxes: settings.textBoxes,
    sumValidation: settings.sumValidation,
    dateFormat: settings.dateFormat,
    imageUrl: settings.imageUrl || bankQuestion.mediaUrl || undefined,
    videoUrl: settings.videoUrl,
    imageDisplaySize: settings.imageDisplaySize || 'original',
    answerPiping: settings.answerPiping,
    textTransfer: settings.textTransfer,
    optionTransfer: settings.optionTransfer,
    translations: {
      pt: {
        title: ptTranslation.title || '',
        description: ptTranslation.description || '',
      },
      en: {
        title: enTranslation?.title || ptTranslation.title || '',
        description: enTranslation?.description || ptTranslation.description || '',
      },
      es: {
        title: esTranslation?.title || ptTranslation.title || '',
        description: esTranslation?.description || ptTranslation.description || '',
      },
    }
  }

  switch (mappedType) {
    case 'multiple':
    case 'single':
    case 'checkbox':
    case 'dropdown':
    case 'ranking':
    case 'bestworst':
      return {
        ...baseQuestion,
        options: options && options.length > 0 ? options : [
          { id: generateId(), text: 'Opção 1' },
          { id: generateId(), text: 'Opção 2' }
        ],
        allowOther: baseQuestion.allowOther ?? false,
        otherLabel: baseQuestion.otherLabel || 'Outro',
        otherPlaceholder: baseQuestion.otherPlaceholder || 'Digite sua resposta',
        requireOtherText: baseQuestion.requireOtherText ?? true,
        trackSelectionOrder: baseQuestion.trackSelectionOrder ?? false,
        randomizeOptions: baseQuestion.randomizeOptions ?? false,
        randomizeFixedLastCount: baseQuestion.randomizeFixedLastCount ?? 0
      }

    case 'classification':
      return {
        ...baseQuestion,
        options: options && options.length > 0 ? options : [
          { id: generateId(), text: 'Opção 1' },
          { id: generateId(), text: 'Opção 2' }
        ],
        trackSelectionOrder: true,
        matrixColumns: undefined,
        matrixRows: undefined
      }

    case 'matrix':
    case 'dropdown_matrix':
      return {
        ...baseQuestion,
        matrixRows: baseQuestion.matrixRows || (options && options.length > 0 ? options : [
          { id: generateId(), text: 'Linha 1' },
          { id: generateId(), text: 'Linha 2' }
        ]).map((option, index) => ({
          id: option.id,
          label: option.text || `Linha ${index + 1}`
        })),
        matrixColumns: baseQuestion.matrixColumns || [
          { id: generateId(), label: 'Coluna 1', value: '1' },
          { id: generateId(), label: 'Coluna 2', value: '2' }
        ]
      }

    case 'rating':
    case 'rating_matrix':
      return {
        ...baseQuestion,
        matrixRows: baseQuestion.matrixRows || (mappedType === 'rating_matrix'
          ? (options && options.length > 0 ? options : [
            { id: generateId(), text: 'Item 1' },
            { id: generateId(), text: 'Item 2' }
          ]).map((option, index) => ({
            id: option.id,
            label: option.text || `Item ${index + 1}`
          }))
          : undefined),
        minValue: baseQuestion.minValue ?? 1,
        maxValue: baseQuestion.maxValue ?? 5,
        minLabel: baseQuestion.minLabel || 'Muito Ruim',
        maxLabel: baseQuestion.maxLabel || 'Excelente',
        ratingIcon: baseQuestion.ratingIcon || 'star',
        ratingColor: baseQuestion.ratingColor || '#facc15',
        ratingSize: baseQuestion.ratingSize || 'medium'
      }

    case 'slider':
    case 'slider_matrix':
      return {
        ...baseQuestion,
        matrixRows: baseQuestion.matrixRows || (mappedType === 'slider_matrix'
          ? (options && options.length > 0 ? options : [
            { id: generateId(), text: 'Item 1' },
            { id: generateId(), text: 'Item 2' }
          ]).map((option, index) => ({
            id: option.id,
            label: option.text || `Item ${index + 1}`
          }))
          : undefined),
        minValue: baseQuestion.minValue ?? 0,
        maxValue: baseQuestion.maxValue ?? 100,
        stepValue: baseQuestion.stepValue ?? 1,
        minLabel: baseQuestion.minLabel || 'Mínimo',
        maxLabel: baseQuestion.maxLabel || 'Máximo'
      }

    case 'multitext':
      return {
        ...baseQuestion,
        textBoxes: baseQuestion.textBoxes || (options && options.length > 0 ? options : [
          { id: generateId(), text: 'Campo 1' },
          { id: generateId(), text: 'Campo 2' }
        ]).map((option, index) => ({
          id: option.id,
          label: option.text || `Campo ${index + 1}`,
          type: 'text',
          required: false
        })),
        sumValidation: baseQuestion.sumValidation || {
          enabled: false,
          targetSum: 100,
          errorMessage: 'A soma deve ser igual a 100'
        }
      }

    case 'datetime':
      return {
        ...baseQuestion,
        dateFormat: 'date'
      }

    case 'number':
      return {
        ...baseQuestion,
        validation: {
          required: bankQuestion.isRequired,
          minValue: undefined,
          maxValue: undefined
        }
      }

    case 'email':
    case 'text':
    case 'opentext':
      return {
        ...baseQuestion,
        validation: {
          required: bankQuestion.isRequired,
          minLength: undefined,
          maxLength: undefined
        }
      }

    default:
      return baseQuestion
  }
}

interface QuestionBuilderProps {
  questions: Question[]
  onChange: (questions: Question[]) => void
  onStructureChange?: (questions: Question[], pages: SurveyPage[]) => void
  onPreview?: (question: Question) => void
  pages?: SurveyPage[]
  onPagesChange?: (pages: SurveyPage[]) => void
}

const QuestionBuilder: React.FC<QuestionBuilderProps> = ({ questions, onChange, onStructureChange, onPreview, pages = [], onPagesChange }) => {
  const [showTypeSelector, setShowTypeSelector] = useState(false)
  const [editingState, setEditingState] = useState<{
    mode: 'create' | 'edit'
    question: Question
    source: 'manual' | 'bank'
  } | null>(null)
  const [showPositionModal, setShowPositionModal] = useState<{ question: Question; action: 'before' | 'after' } | null>(null)
  const [currentPage, setCurrentPage] = useState<string>('all')
  const [showConditionEditor, setShowConditionEditor] = useState<Question | null>(null)
  const [showBranchEditor, setShowBranchEditor] = useState<Question | null>(null)
  const bankInsertPlacementRef = useRef<{
    questionId: string
    pageId: string
    referenceQuestionId?: string
    action: 'before' | 'after' | 'end'
  } | null>(null)

  const generateId = () => Math.random().toString(36).substr(2, 9)

  const normalizedState = useMemo(
    () => normalizeQuestionPageState(questions, pages),
    [questions, pages]
  )

  const normalizedQuestions = normalizedState.questions
  const normalizedPages = normalizedState.pages
  const questionPageMap = useMemo(() => buildQuestionPageMap(normalizedPages), [normalizedPages])
  const orderedQuestions = useMemo(
    () => buildOrderedQuestions(normalizedQuestions, normalizedPages),
    [normalizedQuestions, normalizedPages]
  )

  useEffect(() => {
    if (!onPagesChange) return

    if (questions.length > 0 && pages.length === 0 && normalizedPages.length > 0) {
      onPagesChange(normalizedPages)
    }
  }, [questions.length, pages.length, normalizedPages, onPagesChange])

  const getDefaultTargetPageId = () => {
    if (normalizedPages.length === 0) return undefined

    if (currentPage !== 'all' && normalizedPages.some((page) => page.id === currentPage)) {
      return currentPage
    }

    return normalizedPages[0].id
  }

  const commitState = (
    nextQuestions: Question[],
    nextPages: SurveyPage[],
    options?: { keepEditorOpen?: boolean }
  ) => {
    const normalizedNextState = normalizeQuestionPageState(nextQuestions, nextPages)
    if (onStructureChange) {
      onStructureChange(normalizedNextState.questions, normalizedNextState.pages)
    } else {
      onChange(normalizedNextState.questions)

      if (onPagesChange) {
        onPagesChange(normalizedNextState.pages)
      }
    }

    if (!options?.keepEditorOpen) {
      setEditingState(null)
    }
  }

  const getVisibleQuestions = () => {
    if (currentPage === 'all') {
      return orderedQuestions
    }

    const page = normalizedPages.find((item) => item.id === currentPage)
    if (!page) {
      return orderedQuestions
    }

    const questionMap = new Map(orderedQuestions.map((question) => [question.id, question]))
    return page.questionIds
      .map((questionId) => questionMap.get(questionId))
      .filter((question): question is Question => question !== undefined)
  }

  const visibleQuestions = getVisibleQuestions()

  const getGlobalQuestionNumber = (questionId: string) => {
    return orderedQuestions.findIndex((question) => question.id === questionId) + 1
  }

  const upsertQuestionInPage = (
    sourcePages: SurveyPage[],
    questionId: string,
    targetPageId: string,
    placement?: { beforeQuestionId?: string; afterQuestionId?: string; atStart?: boolean; atEnd?: boolean }
  ) => sourcePages.map((page) => {
    const baseIds = page.questionIds.filter((id) => id !== questionId)

    if (page.id !== targetPageId) {
      return {
        ...page,
        questionIds: baseIds
      }
    }

    const insertIndex = (() => {
      if (placement?.atStart) return 0
      if (placement?.beforeQuestionId) {
        const index = baseIds.indexOf(placement.beforeQuestionId)
        return index >= 0 ? index : baseIds.length
      }
      if (placement?.afterQuestionId) {
        const index = baseIds.indexOf(placement.afterQuestionId)
        return index >= 0 ? index + 1 : baseIds.length
      }
      if (placement?.atEnd) return baseIds.length
      return baseIds.length
    })()

    const nextIds = [...baseIds]
    nextIds.splice(insertIndex, 0, questionId)

    return {
      ...page,
      questionIds: nextIds
    }
  })

  const createManualQuestionDraft = (type: QuestionType, initialOptionCount?: number) => {
    const question = createQuestionByType(type, generateId, {
      pageId: getDefaultTargetPageId()
    }, initialOptionCount)

    setEditingState({
      mode: 'create',
      question,
      source: 'manual'
    })
    setShowTypeSelector(false)
  }

  const openQuestionDraft = (type: QuestionType) => {
    const initialOptionCount = supportsInitialOptionCount(type)
      ? (() => {
        const rawValue = window.prompt(
          'Insira quantidade de opções iniciais para esta pergunta: (mínimo 2, máximo 20)',
          '2'
        )

        if (rawValue === null) {
          return null
        }

        const parsedValue = Number.parseInt(rawValue, 10)

        if (Number.isNaN(parsedValue)) {
          toast.error('Informe uma quantidade valida de opções.')
          return null
        }

        return parsedValue
      })()
      : undefined

    if (initialOptionCount === null) {
      return
    }

    createManualQuestionDraft(type, initialOptionCount)
  }

  const chooseBankQuestionPlacement = (questionId: string) => {
    if (normalizedPages.length === 0) return null

    const pageList = normalizedPages
      .map((page, index) => `${index + 1}. ${stripHtml(page.title) || `Página ${index + 1}`}`)
      .join('\n')
    const rawPage = window.prompt(`Escolha a página onde a pergunta sera inserida:\n${pageList}`, '1')
    if (rawPage === null) return null

    const pageIndex = Math.max(0, Math.min(normalizedPages.length - 1, Number.parseInt(rawPage, 10) - 1))
    const selectedPage = normalizedPages[Number.isNaN(pageIndex) ? 0 : pageIndex]
    const pageQuestions = selectedPage.questionIds
      .map((id) => normalizedQuestions.find((item) => item.id === id))
      .filter((item): item is Question => !!item)

    if (pageQuestions.length === 0) {
      return { questionId, pageId: selectedPage.id, action: 'end' as const }
    }

    const questionList = pageQuestions
      .map((item, index) => `${index + 1}. ${stripHtml(item.title) || `Pergunta ${index + 1}`}`)
      .join('\n')
    const rawReference = window.prompt(
      `Escolha a pergunta de referencia nesta página:\n${questionList}\n\nUse 0 para inserir no final.`,
      '0'
    )

    if (rawReference === null) return null

    const referenceIndex = Number.parseInt(rawReference, 10) - 1
    if (Number.isNaN(referenceIndex) || referenceIndex < 0 || referenceIndex >= pageQuestions.length) {
      return { questionId, pageId: selectedPage.id, action: 'end' as const }
    }

    const rawAction = window.prompt('Inserir antes ou depois da pergunta escolhida? Digite A para antes ou D para depois.', 'D')
    return {
      questionId,
      pageId: selectedPage.id,
      referenceQuestionId: pageQuestions[referenceIndex].id,
      action: rawAction?.trim().toLowerCase().startsWith('a') ? 'before' as const : 'after' as const,
    }
  }

  const openBankQuestionDraft = (bankQuestion: BankQuestion) => {
    const question = createQuestionFromBank(bankQuestion, generateId, getDefaultTargetPageId())
    const placement = chooseBankQuestionPlacement(question.id)
    bankInsertPlacementRef.current = placement

    setEditingState({
      mode: 'create',
      question: placement?.pageId ? { ...question, pageId: placement.pageId } : question,
      source: 'bank'
    })
    setShowTypeSelector(false)
  }

  const openExistingQuestion = (question: Question) => {
    setEditingState({
      mode: 'edit',
      question: { ...question },
      source: 'manual'
    })
  }

  const saveQuestion = (question: Question, mode: 'create' | 'edit', source: 'manual' | 'bank') => {
    const currentPageId = questionPageMap.get(question.id)
    const bankPlacement = source === 'bank' && mode === 'create' && bankInsertPlacementRef.current?.questionId === question.id
      ? bankInsertPlacementRef.current
      : null
    const targetPageId = question.pageId || bankPlacement?.pageId || currentPageId || getDefaultTargetPageId()
    const targetPlacement = bankPlacement?.pageId === targetPageId ? bankPlacement : null
    const questionToSave = {
      ...question,
      pageId: targetPageId
    }

    const nextQuestions = mode === 'create'
      ? [...normalizedQuestions, questionToSave]
      : normalizedQuestions.map((item) => (
        item.id === questionToSave.id ? questionToSave : item
      ))

    let nextPages = normalizedPages

    if (targetPageId && (mode === 'create' || currentPageId !== targetPageId)) {
      if (targetPlacement?.action === 'before' && targetPlacement.referenceQuestionId) {
        nextPages = upsertQuestionInPage(normalizedPages, questionToSave.id, targetPageId, {
          beforeQuestionId: targetPlacement.referenceQuestionId,
        })
      } else if (targetPlacement?.action === 'after' && targetPlacement.referenceQuestionId) {
        nextPages = upsertQuestionInPage(normalizedPages, questionToSave.id, targetPageId, {
          afterQuestionId: targetPlacement.referenceQuestionId,
        })
      } else {
        nextPages = upsertQuestionInPage(normalizedPages, questionToSave.id, targetPageId, { atEnd: true })
      }

      bankInsertPlacementRef.current = null
    }

    commitState(nextQuestions, nextPages)

    if (mode === 'create') {
      toast.success(source === 'bank' ? 'Pergunta importada do banco com sucesso!' : 'Pergunta criada com sucesso!')
      return
    }

    toast.success('Pergunta salva com sucesso!')
  }

  const updateQuestion = (questionId: string, updates: Partial<Question>) => {
    const nextQuestions = normalizedQuestions.map((question) => (
      question.id === questionId ? { ...question, ...updates } : question
    ))

    commitState(nextQuestions, normalizedPages, { keepEditorOpen: true })

    if (editingState?.question.id === questionId) {
      setEditingState((current) => (
        current ? { ...current, question: { ...current.question, ...updates } } : null
      ))
    }
  }

  const deleteQuestion = (questionId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta pergunta?')) {
      return
    }

    const nextQuestions = normalizedQuestions.filter((question) => question.id !== questionId)
    const nextPages = normalizedPages.map((page) => ({
      ...page,
      questionIds: page.questionIds.filter((id) => id !== questionId)
    }))

    commitState(nextQuestions, nextPages)
    toast.success('Pergunta excluída com sucesso!')
  }

  const duplicateQuestion = (question: Question) => {
    const duplicatedQuestion = cloneQuestionForDuplication(question, generateId)
    const targetPageId = questionPageMap.get(question.id) || question.pageId || getDefaultTargetPageId()
    const nextQuestions = [...normalizedQuestions, { ...duplicatedQuestion, pageId: targetPageId }]
    const nextPages = targetPageId
      ? upsertQuestionInPage(normalizedPages, duplicatedQuestion.id, targetPageId, { afterQuestionId: question.id })
      : normalizedPages

    commitState(nextQuestions, nextPages)
    toast.success('Pergunta duplicada com sucesso!')
  }

  const moveQuestionToPosition = (
    questionId: string,
    targetQuestionId: string,
    position: 'before' | 'after'
  ) => {
    if (questionId === targetQuestionId) return

    const targetPageId = questionPageMap.get(targetQuestionId)
    if (!targetPageId) return

    const nextPages = upsertQuestionInPage(normalizedPages, questionId, targetPageId, (
      position === 'before'
        ? { beforeQuestionId: targetQuestionId }
        : { afterQuestionId: targetQuestionId }
    ))

    commitState(normalizedQuestions, nextPages)
    setShowPositionModal(null)
    toast.success(`Pergunta movida ${position === 'before' ? 'antes' : 'depois'} da pergunta selecionada`)
  }

  const moveToExtreme = (questionId: string, position: 'first' | 'last') => {
    if (normalizedPages.length === 0) return

    const targetPage = position === 'first'
      ? normalizedPages[0]
      : normalizedPages[normalizedPages.length - 1]

    const nextPages = upsertQuestionInPage(normalizedPages, questionId, targetPage.id, (
      position === 'first'
        ? { atStart: true }
        : { atEnd: true }
    ))

    commitState(normalizedQuestions, nextPages)
    toast.success(position === 'first' ? 'Pergunta movida para o inicio' : 'Pergunta movida para o final')
  }

  const handleDragEnd = (result: any) => {
    if (!result.destination) return
    if (result.source.index === result.destination.index) return

    const movedQuestion = visibleQuestions[result.source.index]
    const targetQuestion = visibleQuestions[result.destination.index]

    if (!movedQuestion || !targetQuestion || movedQuestion.id === targetQuestion.id) {
      return
    }

    moveQuestionToPosition(
      movedQuestion.id,
      targetQuestion.id,
      result.source.index < result.destination.index ? 'after' : 'before'
    )
  }

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-30 -mx-1 flex flex-col gap-4 border-b border-gray-200 bg-white px-1 py-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Perguntas da Pesquisa ({orderedQuestions.length})
          </h3>

          {normalizedPages.length > 1 && (
            <select
              value={currentPage}
              onChange={(e) => setCurrentPage(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
            >
              <option value="all">Todas as páginas</option>
              {normalizedPages.map((page) => (
                <option key={page.id} value={page.id}>
                  {stripHtml(page.title) || 'Página sem título'}
                </option>
              ))}
            </select>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowTypeSelector(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors"
        >
          <Plus size={20} />
          <span>Adicionar Pergunta</span>
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="questions">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
              {visibleQuestions.map((question, index) => (
                <Draggable key={question.id} draggableId={question.id} index={index}>
                  {(provided) => {
                    const pageId = questionPageMap.get(question.id) || question.pageId
                    const pageTitle = stripHtml(normalizedPages.find((page) => page.id === pageId)?.title || '')
                    const conditionSummaries = getQuestionConditionSummaries(question, orderedQuestions)
                    const branchSummaries = getQuestionBranchSummaries(question, normalizedPages)
                    const typeInfo = questionTypes.find((item) => item.type === question.type)

                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="bg-white rounded-lg border border-gray-200 shadow-sm"
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            <div
                              {...provided.dragHandleProps}
                              className="mt-1 text-gray-400 hover:text-gray-600 cursor-move"
                            >
                              <GripVertical size={20} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="mb-3 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                  <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
                                    #{getGlobalQuestionNumber(question.id)}
                                  </span>
                                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                                    {typeInfo?.label}
                                  </span>
                                  {question.required && (
                                    <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                                      Obrigatória
                                    </span>
                                  )}
                                  {pageTitle && (
                                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 max-w-full">
                                      <Layers size={12} />
                                      <span className="truncate max-w-full">{pageTitle}</span>
                                    </span>
                                  )}
                                  {question.displayConditions && question.displayConditions.length > 0 && (
                                    <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                                      <Filter size={12} />
                                      <span>{question.displayConditions.length} condicao(oes)</span>
                                    </span>
                                  )}
                                  {question.conditionalBranches && question.conditionalBranches.length > 0 && (
                                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                                      <GitBranch size={12} />
                                      <span>{question.conditionalBranches.length} desvio(s)</span>
                                    </span>
                                  )}
                                </div>

                                <div className="flex shrink-0 flex-wrap items-center gap-1 xl:justify-end">
                                  {orderedQuestions.length > 1 && (
                                    <div className="mr-2 flex items-center gap-1 rounded-md bg-gray-50 px-1 py-0.5">
                                      {getGlobalQuestionNumber(question.id) !== 1 && (
                                        <button
                                          type="button"
                                          onClick={() => moveToExtreme(question.id, 'first')}
                                          className="text-gray-500 hover:text-gray-700 p-1"
                                          title="Mover para o inicio"
                                        >
                                          <MoveUp size={14} />
                                        </button>
                                      )}

                                      <button
                                        type="button"
                                        onClick={() => setShowPositionModal({ question, action: 'before' })}
                                        className="text-blue-500 hover:text-blue-700 p-1"
                                        title="Posicionar antes de..."
                                      >
                                        <ArrowUp size={14} />
                                      </button>

                                      <button
                                        type="button"
                                        onClick={() => setShowPositionModal({ question, action: 'after' })}
                                        className="text-green-500 hover:text-green-700 p-1"
                                        title="Posicionar depois de..."
                                      >
                                        <ArrowDown size={14} />
                                      </button>

                                      {getGlobalQuestionNumber(question.id) !== orderedQuestions.length && (
                                        <button
                                          type="button"
                                          onClick={() => moveToExtreme(question.id, 'last')}
                                          className="text-gray-500 hover:text-gray-700 p-1"
                                          title="Mover para o final"
                                        >
                                          <MoveDown size={14} />
                                        </button>
                                      )}
                                    </div>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() => setShowConditionEditor(question)}
                                    className="text-purple-600 hover:text-purple-800 p-1"
                                    title="Definir condicoes"
                                  >
                                    <Filter size={16} />
                                  </button>
                                  {normalizedPages.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => setShowBranchEditor(question)}
                                      className="text-indigo-600 hover:text-indigo-800 p-1"
                                      title="Desvios de página"
                                    >
                                      <GitBranch size={16} />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openExistingQuestion(question)}
                                    className="text-blue-600 hover:text-blue-800 p-1"
                                    title="Editar pergunta"
                                  >
                                    <Settings size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => onPreview?.(question)}
                                    className="text-green-600 hover:text-green-800 p-1"
                                    title="Visualizar pergunta"
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => duplicateQuestion(question)}
                                    className="text-gray-600 hover:text-gray-800 p-1"
                                    title="Duplicar pergunta"
                                  >
                                    <Copy size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteQuestion(question.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Excluir pergunta"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>

                              <h4 className="mb-1 font-medium text-gray-900 whitespace-normal break-words [overflow-wrap:anywhere]">
                                {stripHtml(question.title) || 'Pergunta sem título'}
                              </h4>
                              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                                {question.variable?.trim() ? (
                                  <span className="rounded bg-indigo-100 px-2 py-1 font-semibold text-indigo-800">Variável: {question.variable.trim()}</span>
                                ) : (
                                  <span className="rounded bg-amber-100 px-2 py-1 font-medium text-amber-800">Variável pendente</span>
                                )}
                              </div>
                              {typeInfo?.description && (
                                <p className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-gray-400">
                                  {typeInfo.description}
                                </p>
                              )}
                              {question.description && (
                                <p className="mb-2 text-sm text-gray-600 whitespace-normal break-words [overflow-wrap:anywhere]">
                                  {stripHtml(question.description)}
                                </p>
                              )}

                              {question.imageUrl && (
                                <div className="mb-3 rounded-lg border border-sky-100 bg-sky-50 p-3">
                                  <p className="text-xs text-sky-700">
                                    Imagem configurada para exibicao nesta pergunta.
                                  </p>
                                  <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_220px] md:items-end">
                                    <div className="text-xs text-sky-800">
                                      Tamanho atual: <strong>{getImageDisplayHint(question.imageDisplaySize)}</strong>
                                    </div>
                                    <div>
                                      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-sky-800">
                                        Tamanho da imagem
                                      </label>
                                      <select
                                        value={question.imageDisplaySize || 'original'}
                                        onChange={(event) => updateQuestion(question.id, {
                                          imageDisplaySize: event.target.value as Question['imageDisplaySize']
                                        })}
                                        className="w-full rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      >
                                        <option value="small">Pequeno</option>
                                        <option value="medium">Médio</option>
                                        <option value="large">Grande</option>
                                        <option value="original">Original</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {question.options && question.options.length > 0 && (
                                <div className="min-w-0 text-sm text-gray-500">
                                  <span className="block whitespace-normal break-words [overflow-wrap:anywhere]">
                                    Opções:
                                  </span>
                                  <div className="mt-1 space-y-1 pl-4">
                                    {question.options.map((option) => (
                                      <div
                                        key={option.id}
                                        className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]"
                                      >
                                        {stripHtml(option.text)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {question.type === 'multitext' && question.textBoxes && (
                                <div className="text-sm text-gray-500 whitespace-normal break-words [overflow-wrap:anywhere]">
                                  Campos: {question.textBoxes.map((box) => box.label).join(', ')}
                                  {question.sumValidation?.enabled && (
                                    <span className="ml-2 text-blue-600">
                                      (Soma: {question.sumValidation.targetSum})
                                    </span>
                                  )}
                                </div>
                              )}

                              {conditionSummaries.length > 0 && (
                                <div className="mt-3 space-y-1 rounded-lg border border-orange-100 bg-orange-50 p-3 text-xs text-orange-800">
                                  {conditionSummaries.map((summary, summaryIndex) => (
                                    <p key={`${question.id}_cond_${summaryIndex}`} className="whitespace-normal break-words [overflow-wrap:anywhere]">
                                      {summary}
                                    </p>
                                  ))}
                                </div>
                              )}

                              {branchSummaries.length > 0 && (
                                <div className="mt-3 space-y-1 rounded-lg border border-purple-100 bg-purple-50 p-3 text-xs text-purple-800">
                                  {branchSummaries.map((summary, summaryIndex) => (
                                    <p key={`${question.id}_branch_${summaryIndex}`} className="whitespace-normal break-words [overflow-wrap:anywhere]">
                                      {summary}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  }}
                </Draggable>
              ))}
              {provided.placeholder}

              {orderedQuestions.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-gray-400 mb-4">
                    <Type size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Nenhuma pergunta criada
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Comece adicionando sua primeira pergunta a pesquisa
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowTypeSelector(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Adicionar Primeira Pergunta
                  </button>
                </div>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Modal de posicionamento */}
      {showPositionModal && (
        <QuestionPositionModal
          question={showPositionModal.question}
          action={showPositionModal.action}
          availableQuestions={orderedQuestions.filter((question) => question.id !== showPositionModal.question.id)}
          onConfirm={(targetQuestionId) => {
            moveQuestionToPosition(showPositionModal.question.id, targetQuestionId, showPositionModal.action)
          }}
          onClose={() => setShowPositionModal(null)}
        />
      )}

      {/* Modal de seleção de tipo */}
      {showTypeSelector && (
        <QuestionTypeSelector
          onSelect={openQuestionDraft}
          onBankQuestionSelect={openBankQuestionDraft}
          onClose={() => setShowTypeSelector(false)}
        />
      )}

      {/* Modal de edição */}
      {editingState && (
        <QuestionEditor
          question={editingState.question}
          mode={editingState.mode}
          onSave={(question) => {
            saveQuestion(question, editingState.mode, editingState.source)
          }}
          onClose={() => setEditingState(null)}
          allQuestions={normalizedQuestions}
          pages={normalizedPages}
        />
      )}

      {/* Modal de condiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes */}
      {showConditionEditor && (
        <ConditionEditor
          question={showConditionEditor}
          allQuestions={orderedQuestions}
          onSave={(conditions, logic) => {
            updateQuestion(showConditionEditor.id, {
              displayConditions: conditions,
              displayLogic: logic
            })
            setShowConditionEditor(null)
            toast.success('Condicoes salvas com sucesso!')
          }}
          onClose={() => setShowConditionEditor(null)}
        />
      )}

      {/* Modal de desvios de pÃƒÆ’Ã‚Â¡gina */}
      {showBranchEditor && (
        <BranchEditor
          question={showBranchEditor}
          questions={normalizedQuestions}
          pages={normalizedPages}
          onSave={(branches) => {
            updateQuestion(showBranchEditor.id, {
              conditionalBranches: branches
            })
            setShowBranchEditor(null)
            toast.success('Desvios salvos com sucesso!')
          }}
          onClose={() => setShowBranchEditor(null)}
        />
      )}
    </div>
  )
}

// Modal de posicionamento de pergunta
const QuestionPositionModal: React.FC<{
  question: Question
  action: 'before' | 'after'
  availableQuestions: Question[]
  onConfirm: (targetQuestionId: string) => void
  onClose: () => void
}> = ({ question, action, availableQuestions, onConfirm, onClose }) => {
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>('')

  // FunÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o para remover HTML tags para preview
  const stripHtml = (html: string) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  // FunÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o para prevenir fechamento acidental do modal
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  // FunÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o para fechar modal apenas quando clicar no backdrop
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleConfirm = () => {
    if (!selectedQuestionId) {
      toast.error('Selecione uma pergunta de referencia')
      return
    }
    onConfirm(selectedQuestionId)
  }

  return (
    <>
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Posicionar Pergunta
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-blue-900 mb-2">Pergunta a ser movida:</h3>
              <p className="text-blue-800">{stripHtml(question.title) || 'Pergunta sem título'}</p>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Posicionar {action === 'before' ? 'ANTES' : 'DEPOIS'} de qual pergunta?
              </h3>
              
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableQuestions.map((q, index) => (
                  <label key={q.id} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="targetQuestion"
                      value={q.id}
                      checked={selectedQuestionId === q.id}
                      onChange={(e) => setSelectedQuestionId(e.target.value)}
                      className="mt-1 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
                          #{index + 1}
                        </span>
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                          {questionTypes.find(t => t.type === q.type)?.label}
                        </span>
                      </div>
                      <p className="text-gray-900 font-medium">
                        {stripHtml(q.title) || 'Pergunta sem título'}
                      </p>
                      {q.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {stripHtml(q.description)}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {availableQuestions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>Não ha outras perguntas disponiveis para posicionamento.</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedQuestionId}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {action === 'before' ? 'Mover Antes' : 'Mover Depois'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

// Componente para seleÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o do tipo de pergunta
const QuestionTypeSelector: React.FC<{
  onSelect: (type: QuestionType) => void
  onBankQuestionSelect: (question: BankQuestion) => void
  onClose: () => void
}> = ({ onSelect, onBankQuestionSelect, onClose }) => {
  const [showBankSearch, setShowBankSearch] = useState(false)
  // FunÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o para prevenir fechamento acidental do modal
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  // FunÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o para fechar modal apenas quando clicar no backdrop
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Selecionar Tipo de Pergunta</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          {/* BotÃƒÆ’Ã‚Â£o de buscar no banco de perguntas */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowBankSearch(true)}
              className="w-full p-4 border-2 border-blue-500 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center space-x-3"
            >
              <Search size={20} className="text-blue-600" />
              <span className="font-medium text-blue-900">Buscar no Banco de Perguntas</span>
            </button>
          </div>

          <div className="text-center text-sm text-gray-500 mb-4">ou criar uma nova pergunta</div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleQuestionTypes.map((questionType) => {
              const Icon = questionType.icon
              return (
                <button
                  key={questionType.type}
                  type="button"
                  onClick={() => onSelect(questionType.type)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Icon size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">
                        {questionType.label}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {questionType.description}
                      </p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Modal de busca no banco de perguntas */}
        {showBankSearch && (
          <QuestionBankSearch
            onSelect={(bankQuestion) => {
              setShowBankSearch(false)
              onBankQuestionSelect(bankQuestion)
            }}
            onClose={() => setShowBankSearch(false)}
          />
        )}
      </div>
    </div>
  )
}

// Componente editor de pergunta com Rich Text Editor
const QuestionEditor: React.FC<{
  question: Question
  mode: 'create' | 'edit'
  onSave: (question: Question) => void
  onClose: () => void
  allQuestions?: Question[]
  pages?: SurveyPage[]
}> = ({ question, mode, onSave, onClose, allQuestions = [], pages = [] }) => {
  type EditableQuestion = Question & {
    answerPiping?: AnswerPipingConfig
    textTransfer?: AnswerPipingConfig
    optionTransfer?: OptionTransferConfig
  }

  const normalizeEditableQuestion = (sourceQuestion: Question): EditableQuestion => ({
    ...(sourceQuestion as EditableQuestion),
    type: isQuestionType(sourceQuestion.type)
      ? sourceQuestion.type
      : mapBankQuestionType(String(sourceQuestion.type || 'open_text')),
    answerPiping: (sourceQuestion as any).answerPiping || (sourceQuestion as any).textTransfer,
    textTransfer: (sourceQuestion as any).textTransfer || (sourceQuestion as any).answerPiping,
    optionTransfer: (sourceQuestion as any).optionTransfer
  })

  const [formData, setFormData] = useState<EditableQuestion>(() => normalizeEditableQuestion(question))
  const [pipeSourceId, setPipeSourceId] = useState('')
  const [pipeSourceByBoxId, setPipeSourceByBoxId] = useState<Record<string, string>>({})
  const [uploadingImage, setUploadingImage] = useState(false)
  const [saveToQuestionBank, setSaveToQuestionBank] = useState(false)
  const [itemConditionTarget, setItemConditionTarget] = useState<{
    kind: 'option' | 'textBox' | 'matrixRow' | 'matrixColumn'
    id: string
    label: string
    conditions?: DisplayCondition[]
    logic?: 'AND' | 'OR'
  } | null>(null)
  const generateId = () => Math.random().toString(36).substr(2, 9)

  useEffect(() => {
    setFormData(normalizeEditableQuestion(question))
    setPipeSourceId('')
    setPipeSourceByBoxId({})
    setUploadingImage(false)
    setSaveToQuestionBank(false)
    setItemConditionTarget(null)
  }, [question])

  const availableSourceQuestions = allQuestions.filter((item) => item.id !== formData.id)
  const availableOptionTransferSources = availableSourceQuestions.filter((item) =>
    ['multiple', 'single', 'checkbox', 'dropdown', 'ranking', 'classification', 'bestworst', 'multitext'].includes(item.type)
  )
  const canFilterOptions = ['multiple', 'single', 'checkbox', 'dropdown', 'ranking', 'classification', 'bestworst', 'multitext'].includes(formData.type)
  const canLimitSelections = supportsSelectionLimits(formData.type)
  const selectedPipeSourceId =
    pipeSourceId ||
    formData.optionTransfer?.sourceQuestionId ||
    formData.optionTransfer?.rules?.[0]?.sourceQuestionId ||
    availableOptionTransferSources[0]?.id ||
    availableSourceQuestions[0]?.id ||
    ''

  const getQuestionCode = (questionId: string) => {
    const questionIndex = allQuestions.findIndex((item) => item.id === questionId)
    return questionIndex >= 0 ? `Q${questionIndex + 1}` : questionId
  }

  const getQuestionIdFromCode = (questionCode: string) => {
    const match = questionCode.match(/^Q(\d+)$/i)
    const questionIndex = match ? Number(match[1]) - 1 : -1
    const sourceQuestion = questionIndex >= 0 ? allQuestions[questionIndex] : undefined
    return sourceQuestion && sourceQuestion.id !== formData.id ? sourceQuestion.id : ''
  }

  const getPipeSourceIdForTextBox = (box: MultiTextBox) => {
    const selectedSourceId = pipeSourceByBoxId[box.id]
    if (selectedSourceId && availableSourceQuestions.some((item) => item.id === selectedSourceId)) {
      return selectedSourceId
    }

    const tokenMatch = box.label.match(/\[Q(\d+)(?::\d+)?\]/i)
    const tokenSourceId = tokenMatch ? getQuestionIdFromCode(`Q${tokenMatch[1]}`) : ''
    return tokenSourceId || availableSourceQuestions[0]?.id || ''
  }

  const getQuestionSelectorLabel = (item: Question) => {
    const questionCode = getQuestionCode(item.id)
    const questionTitle = stripHtml(item.title) || 'Pergunta sem título'
    return `${questionCode} - ${questionTitle}`
  }

  const getOptionTransferRules = () => {
    if (formData.optionTransfer?.rules?.length) {
      return formData.optionTransfer.rules
    }

    if (formData.optionTransfer?.sourceQuestionId && formData.optionTransfer.mode) {
      return [{
        sourceQuestionId: formData.optionTransfer.sourceQuestionId,
        mode: formData.optionTransfer.mode
      }]
    }

    return []
  }

  const updateOptionTransferRule = (
    index: number,
    updates: Partial<{ sourceQuestionId: string; mode: OptionTransferConfig['mode'] }>
  ) => {
    setFormData((previous) => {
      if (!previous.optionTransfer) return previous

      const rules = previous.optionTransfer.rules?.length
        ? [...previous.optionTransfer.rules]
        : previous.optionTransfer.sourceQuestionId
          ? [{
            sourceQuestionId: previous.optionTransfer.sourceQuestionId,
            mode: previous.optionTransfer.mode
          }]
          : []

      if (!rules[index]) return previous

      rules[index] = { ...rules[index], ...updates }
      const firstRule = rules[0]

      return {
        ...previous,
        optionTransfer: {
          ...previous.optionTransfer,
          sourceQuestionId: firstRule.sourceQuestionId,
          mode: firstRule.mode,
          rules
        }
      }
    })
  }

  const addOptionTransferRule = () => {
    const fallbackQuestionId = availableOptionTransferSources[0]?.id || ''
    if (!fallbackQuestionId) return

    setFormData((previous) => {
      if (!previous.optionTransfer) return previous

      const rules = previous.optionTransfer.rules?.length
        ? previous.optionTransfer.rules
        : previous.optionTransfer.sourceQuestionId
          ? [{
            sourceQuestionId: previous.optionTransfer.sourceQuestionId,
            mode: previous.optionTransfer.mode
          }]
          : []

      const nextRules = [
        ...rules,
        {
          sourceQuestionId: fallbackQuestionId,
          mode: previous.optionTransfer.mode || 'exclude_selected'
        }
      ]

      return {
        ...previous,
        optionTransfer: {
          ...previous.optionTransfer,
          sourceQuestionId: nextRules[0].sourceQuestionId,
          mode: nextRules[0].mode,
          rules: nextRules
        }
      }
    })
  }

  const removeOptionTransferRule = (index: number) => {
    setFormData((previous) => {
      if (!previous.optionTransfer) return previous

      const rules = getOptionTransferRules().filter((_, ruleIndex) => ruleIndex !== index)
      if (rules.length === 0) {
        return {
          ...previous,
          optionTransfer: undefined
        }
      }

      return {
        ...previous,
        optionTransfer: {
          ...previous.optionTransfer,
          sourceQuestionId: rules[0].sourceQuestionId,
          mode: rules[0].mode,
          rules
        }
      }
    })
  }

  const upsertAnswerPiping = (updates: Partial<AnswerPipingConfig>) => {
    setFormData((previous) => {
      const nextConfig = {
        ...(previous.answerPiping || previous.textTransfer || {}),
        ...updates
      }

      return {
        ...previous,
        answerPiping: nextConfig,
        textTransfer: nextConfig
      }
    })
  }

  const disableAnswerPiping = () => {
    setFormData((previous) => ({
      ...previous,
      answerPiping: undefined,
      textTransfer: undefined
    }))
  }

  const buildResponseToken = (sourceQuestionId: string, position?: number, allAnswers = false) => {
    const code = getQuestionCode(sourceQuestionId)
    if (position) return `[${code}:${position}]`
    if (allAnswers) return `[${code}]`
    const sourceQuestion = allQuestions.find((item) => item.id === sourceQuestionId) ||
      availableSourceQuestions.find((item) => item.id === sourceQuestionId)
    const multiTypes = ['multiple', 'checkbox', 'classification', 'ranking']
    // Para multiplas, o padrao e a 1a resposta selecionada (evita jogar todas no titulo)
    if (sourceQuestion && multiTypes.includes(sourceQuestion.type)) {
      return `[${code}:1]`
    }
    return `[${code}]`
  }

  const insertResponseToken = (field: 'titleTemplate' | 'descriptionTemplate', sourceQuestionId: string, position?: number, allAnswers = false) => {
    if (!sourceQuestionId) {
      toast.error('Selecione primeiro a pergunta de origem.')
      return
    }

    const token = buildResponseToken(sourceQuestionId, position, allAnswers)
    const currentValue = formData.answerPiping?.[field] || (field === 'titleTemplate' ? formData.title : formData.description || '')
    const nextValue = currentValue ? `${currentValue} ${token}` : token
    upsertAnswerPiping({ [field]: nextValue })
  }

  const insertPipeTokenIntoTextBox = (boxId: string, sourceQuestionId: string, position?: number) => {
    if (!sourceQuestionId) {
      toast.error('Selecione primeiro a pergunta de origem no bloco de pipe (ou use o seletor ao lado do campo).')
      return
    }
    const token = buildResponseToken(sourceQuestionId, position, false)
    setFormData((previous) => ({
      ...previous,
      textBoxes: previous.textBoxes?.map((box) =>
        box.id === boxId
          ? { ...box, label: box.label ? `${box.label} ${token}` : token }
          : box
      )
    }))
    toast.success(`Token ${token} inserido no rotulo do campo.`)
  }

  const enableOptionTransfer = () => {
    if (!availableOptionTransferSources.length) {
      toast.error('Crie antes uma pergunta de origem com opções.')
      return
    }

    setFormData((previous) => ({
      ...previous,
      optionTransfer: previous.optionTransfer || {
        sourceQuestionId: availableOptionTransferSources[0].id,
        mode: 'exclude_selected',
        logic: 'AND',
        rules: [{
          sourceQuestionId: availableOptionTransferSources[0].id,
          mode: 'exclude_selected'
        }]
      }
    }))
  }

  const disableOptionTransfer = () => {
    setFormData((previous) => ({
      ...previous,
      optionTransfer: undefined
    }))
  }

  // FunÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o para prevenir fechamento acidental do modal
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  // FunÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o para fechar modal apenas quando clicar no backdrop
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Selecione apenas arquivos de imagem.')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB.')
      return
    }

    try {
      setUploadingImage(true)
      const result = await lumi.tools.file.upload([file])
      const uploadedUrl = result?.[0]?.fileUrl

      if (!uploadedUrl) {
        throw new Error(result?.[0]?.uploadError || 'Falha ao enviar imagem.')
      }

      setFormData((previous) => ({
        ...previous,
        imageUrl: uploadedUrl
      }))
      toast.success('Imagem enviada com sucesso!')
    } catch (error) {
      console.error('Erro ao enviar imagem da pergunta:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar imagem.')
    } finally {
      setUploadingImage(false)
    }
  }

  const handleRemoveImage = () => {
    setFormData((previous) => ({
      ...previous,
      imageUrl: ''
    }))
  }

  const updateSelectionLimit = (field: 'minSelections' | 'maxSelections', rawValue: string) => {
    const parsedValue = rawValue === '' ? undefined : Math.max(0, Math.floor(Number(rawValue)))

    setFormData((previous) => ({
      ...previous,
      validation: {
        ...(previous.validation || { required: previous.required }),
        required: previous.validation?.required ?? previous.required,
        [field]: typeof parsedValue === 'number' && Number.isFinite(parsedValue) ? parsedValue : undefined
      }
    }))
  }

  const validateSelectionLimits = () => {
    if (!canLimitSelections) return true

    const minSelections = formData.validation?.minSelections
    const maxSelections = formData.validation?.maxSelections
    const optionCount = formData.options?.length || 0

    if (
      typeof minSelections === 'number' &&
      typeof maxSelections === 'number' &&
      minSelections > maxSelections
    ) {
      toast.error('O mínimo de seleções não pode ser maior que o máximo.')
      return false
    }

    if (typeof maxSelections === 'number' && optionCount > 0 && maxSelections > optionCount) {
      toast.error('O máximo de seleções não pode ser maior que a quantidade de opções.')
      return false
    }

    return true
  }

  const validateQuestionVariable = () => {
    const variable = formData.variable?.trim() || ''

    // Perguntas já existentes continuam utilizáveis sem variável, mas toda nova
    // pergunta precisa recebê-la antes de ser incluída na pesquisa.
    if (!variable) {
      if (mode === 'create') {
        toast.error('A variável da pergunta é obrigatória.')
        return false
      }
      return true
    }

    if (/\s/.test(variable)) {
      toast.error('A variável não pode conter espaços. Use letras, números, _ ou -.')
      return false
    }

    if (!/^[A-Za-z0-9_-]+$/.test(variable)) {
      toast.error('A variável aceita apenas letras, números, _ e -.')
      return false
    }

    const duplicate = allQuestions.some((question) => (
      question.id !== formData.id && question.variable?.trim().toLocaleLowerCase('pt-BR') === variable.toLocaleLowerCase('pt-BR')
    ))
    if (duplicate) {
      toast.error('Esta variável já está sendo usada por outra pergunta da pesquisa.')
      return false
    }

    return true
  }

  const buildQuestionForSave = (): Question => {
    const normalizedPiping = (() => {
      const config = formData.answerPiping || formData.textTransfer
      const titleTemplate = config?.titleTemplate?.trim()
      const descriptionTemplate = config?.descriptionTemplate?.trim()

      if (!titleTemplate && !descriptionTemplate) {
        return undefined
      }

      return {
        titleTemplate: titleTemplate || undefined,
        descriptionTemplate: descriptionTemplate || undefined
      }
    })()

    const normalizedOptionTransfer = (() => {
      const config = formData.optionTransfer
      const rules = config?.rules?.filter((rule) => rule.sourceQuestionId && rule.mode) || []

      if (rules.length > 0) {
        return {
          sourceQuestionId: rules[0].sourceQuestionId,
          mode: rules[0].mode,
          logic: config?.logic || 'AND',
          rules
        }
      }

      if (!config?.sourceQuestionId || !config.mode) {
        return undefined
      }

      return {
        sourceQuestionId: config.sourceQuestionId,
        mode: config.mode,
        logic: config.logic || 'AND',
        rules: [{
          sourceQuestionId: config.sourceQuestionId,
          mode: config.mode
        }]
      }
    })()

    const normalizedTranslations: Question['translations'] = {
      ...formData.translations,
      pt: {
        ...(formData.translations?.pt || {}),
        title: formData.title,
        description: formData.description || '',
      },
      en: {
        ...(formData.translations?.en || {}),
        title: formData.translations?.en?.title || formData.title,
        description: formData.translations?.en?.description || formData.description || '',
      },
      es: {
        ...(formData.translations?.es || {}),
        title: formData.translations?.es?.title || formData.title,
        description: formData.translations?.es?.description || formData.description || '',
      },
    }

    const normalizedOptions = formData.options?.map((option) => ({
      ...option,
      translations: {
        ...(option.translations || {}),
        pt: option.text,
        en: option.translations?.en || option.text,
        es: option.translations?.es || option.text,
      }
    }))

    const normalizedValidation: QuestionValidation | undefined = (() => {
      if (!formData.validation) return undefined

      if (!supportsSelectionLimits(formData.type)) {
        return {
          ...formData.validation,
          minSelections: undefined,
          maxSelections: undefined,
        }
      }

      return {
        ...formData.validation,
        required: formData.required,
        minSelections: typeof formData.validation.minSelections === 'number'
          ? Math.max(0, Math.floor(formData.validation.minSelections))
          : undefined,
        maxSelections: typeof formData.validation.maxSelections === 'number'
          ? Math.max(0, Math.floor(formData.validation.maxSelections))
          : undefined,
      }
    })()

    const normalizedAllowOther = formData.allowOther || false
    const normalizedFixedLastCount = formData.randomizeOptions
      ? Math.min(
        formData.options?.length || 0,
        Math.max(0, Math.floor(Number(formData.randomizeFixedLastCount || 0)))
      )
      : 0

    return {
      ...formData,
      variable: formData.variable?.trim() || undefined,
      options: normalizedOptions,
      validation: normalizedValidation,
      allowOther: normalizedAllowOther,
      otherLabel: normalizedAllowOther ? (formData.otherLabel?.trim() || 'Outro') : undefined,
      otherPlaceholder: normalizedAllowOther ? (formData.otherPlaceholder?.trim() || 'Digite sua resposta') : undefined,
      randomizeFixedLastCount: normalizedFixedLastCount,
      imageDisplaySize: formData.imageDisplaySize || 'original',
      translations: normalizedTranslations,
      answerPiping: normalizedPiping,
      textTransfer: normalizedPiping,
      optionTransfer: normalizedOptionTransfer
    } as Question
  }

  const mapInternalTypeToBankType = (type: QuestionType) => {
    const typeMap: Partial<Record<QuestionType, string>> = {
      multiple: 'multiple_choice',
      single: 'single_choice',
      checkbox: 'multiple_select',
      ranking: 'ordered_multiple',
      dropdown: 'dropdown',
      dropdown_matrix: 'dropdown_matrix',
      matrix: 'matrix_dropdown',
      media: 'media',
      rating: 'star_rating',
      rating_matrix: 'rating_matrix',
      slider: 'slider',
      slider_matrix: 'slider_matrix',
      classification: 'classification',
      bestworst: 'best_worst',
      text: 'open_text',
      number: 'open_number',
      multitext: 'multiple_textboxes',
      datetime: 'datetime',
      email: 'email',
    }

    return typeMap[type] || 'open_text'
  }

  const saveQuestionToBank = async (questionToSave: Question) => {
    if (!saveToQuestionBank) return

    try {
      const bankSettings = {
        internalType: questionToSave.type,
        options: questionToSave.options,
        validation: questionToSave.validation,
        allowOther: questionToSave.allowOther,
        otherLabel: questionToSave.otherLabel,
        otherPlaceholder: questionToSave.otherPlaceholder,
        requireOtherText: questionToSave.requireOtherText,
        trackSelectionOrder: questionToSave.trackSelectionOrder,
        randomizeOptions: questionToSave.randomizeOptions,
        randomizeFixedLastCount: questionToSave.randomizeFixedLastCount,
        multipleColumns: questionToSave.multipleColumns,
        matrixRows: questionToSave.matrixRows,
        matrixColumns: questionToSave.matrixColumns,
        minValue: questionToSave.minValue,
        maxValue: questionToSave.maxValue,
        minLabel: questionToSave.minLabel,
        maxLabel: questionToSave.maxLabel,
        stepValue: questionToSave.stepValue,
        ratingIcon: questionToSave.ratingIcon,
        ratingColor: questionToSave.ratingColor,
        ratingSize: questionToSave.ratingSize,
        textBoxes: questionToSave.textBoxes,
        sumValidation: questionToSave.sumValidation,
        dateFormat: questionToSave.dateFormat,
        imageUrl: questionToSave.imageUrl,
        videoUrl: questionToSave.videoUrl,
        imageDisplaySize: questionToSave.imageDisplaySize,
        answerPiping: questionToSave.answerPiping,
        textTransfer: questionToSave.textTransfer,
        optionTransfer: questionToSave.optionTransfer,
      }

      await lumi.entities.questions.create({
        type: mapInternalTypeToBankType(questionToSave.type),
        translations: {
          pt: {
            title: questionToSave.title,
            description: questionToSave.description || '',
            options: (questionToSave.options || []).map((option) => option.text || ''),
          },
          en: {
            title: questionToSave.translations?.en?.title || questionToSave.title,
            description: questionToSave.translations?.en?.description || questionToSave.description || '',
            options: (questionToSave.options || []).map((option) => option.translations?.en || option.text || ''),
          },
          es: {
            title: questionToSave.translations?.es?.title || questionToSave.title,
            description: questionToSave.translations?.es?.description || questionToSave.description || '',
            options: (questionToSave.options || []).map((option) => option.translations?.es || option.text || ''),
          },
        },
        isRequired: questionToSave.required,
        category: '',
        tags: [],
        settings: bankSettings,
        mediaUrl: questionToSave.imageUrl || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      toast.success('Pergunta tambem salva no banco de perguntas.')
    } catch (error) {
      console.error('Erro ao salvar pergunta no banco:', error)
      toast.error('A pergunta foi salva na pesquisa, mas não foi possível salvar no banco.')
    }
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (uploadingImage) {
      toast.error('Aguarde o upload da imagem terminar.')
      return
    }
    
    // Remover formataÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o HTML do tÃƒÆ’Ã‚Â­tulo para validaÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o
    const tmpDiv = document.createElement('div')
    tmpDiv.innerHTML = formData.title
    const plainTitle = tmpDiv.textContent || tmpDiv.innerText || ''
    
    if (!plainTitle.trim()) {
      toast.error('O título da pergunta é obrigatório')
      return
    }

    if (!validateQuestionVariable()) return

    if (!validateSelectionLimits()) {
      return
    }
    
    const questionToSave = buildQuestionForSave()
    onSave(questionToSave)
    void saveQuestionToBank(questionToSave)
    // NÃƒÆ’Ã‚Â£o fechar o modal aqui - deixar o componente pai decidir
  }

  const handleSaveAndClose = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (uploadingImage) {
      toast.error('Aguarde o upload da imagem terminar.')
      return
    }
    
    // Remover formataÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o HTML do tÃƒÆ’Ã‚Â­tulo para validaÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o
    const tmpDiv = document.createElement('div')
    tmpDiv.innerHTML = formData.title
    const plainTitle = tmpDiv.textContent || tmpDiv.innerText || ''
    
    if (!plainTitle.trim()) {
      toast.error('O título da pergunta é obrigatório')
      return
    }

    if (!validateQuestionVariable()) return

    if (!validateSelectionLimits()) {
      return
    }
    
    const questionToSave = buildQuestionForSave()
    onSave(questionToSave)
    void saveQuestionToBank(questionToSave)
    onClose()
  }

  const addOption = () => {
    const newOption = {
      id: Math.random().toString(36).substr(2, 9),
      text: '',
      translations: {
        pt: '',
        en: '',
        es: '',
      }
    }
    setFormData(prev => ({
      ...prev,
      options: [...(prev.options || []), newOption]
    }))
  }

  const updateOption = (optionId: string, text: string) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.map(opt => 
        opt.id === optionId ? {
          ...opt,
          text,
          translations: {
            ...(opt.translations || {}),
            pt: text,
          }
        } : opt
      )
    }))
  }

  const updateOptionExclusive = (optionId: string, isExclusive: boolean) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.map(opt => (
        opt.id === optionId
          ? { ...opt, isExclusive }
          : opt
      ))
    }))
  }

  const moveOption = (optionId: string, direction: 'up' | 'down') => {
    setFormData((prev) => {
      const options = [...(prev.options || [])]
      const currentIndex = options.findIndex((option) => option.id === optionId)
      if (currentIndex < 0) return prev

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      if (targetIndex < 0 || targetIndex >= options.length) return prev

      const [moved] = options.splice(currentIndex, 1)
      options.splice(targetIndex, 0, moved)

      return {
        ...prev,
        options,
      }
    })
  }

  const sortOptionsAlphabetically = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...(prev.options || [])].sort((left, right) => (
        stripHtml(left.text).localeCompare(stripHtml(right.text), 'pt-BR', { sensitivity: 'base' })
      ))
    }))
  }

  const removeOption = (optionId: string) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.filter(opt => opt.id !== optionId)
    }))
  }

  const openItemConditionEditor = (
    kind: 'option' | 'textBox' | 'matrixRow' | 'matrixColumn',
    item: QuestionOption | MultiTextBox | MatrixRow | MatrixColumn
  ) => {
    const label = kind === 'option'
      ? stripHtml((item as QuestionOption).text || '')
      : (item as MultiTextBox | MatrixRow | MatrixColumn).label

    setItemConditionTarget({
      kind,
      id: item.id,
      label: label || 'Item sem título',
      conditions: (item as any).displayConditions || [],
      logic: (item as any).displayLogic || 'AND'
    })
  }

  const saveItemConditions = (conditions: DisplayCondition[], logic: 'AND' | 'OR') => {
    if (!itemConditionTarget) return

    const applyConditions = <T extends { id: string }>(item: T) => (
      item.id === itemConditionTarget.id
        ? {
          ...item,
          displayConditions: conditions.length > 0 ? conditions : undefined,
          displayLogic: conditions.length > 0 ? logic : undefined,
        }
        : item
    )

    setFormData((previous) => {
      switch (itemConditionTarget.kind) {
        case 'option':
          return { ...previous, options: previous.options?.map(applyConditions) }
        case 'textBox':
          return { ...previous, textBoxes: previous.textBoxes?.map(applyConditions) }
        case 'matrixRow':
          return { ...previous, matrixRows: previous.matrixRows?.map(applyConditions) }
        case 'matrixColumn':
          return { ...previous, matrixColumns: previous.matrixColumns?.map(applyConditions) }
        default:
          return previous
      }
    })

    setItemConditionTarget(null)
    toast.success('Condicoes do item salvas com sucesso!')
  }

  const addTextBox = () => {
    const newTextBox = { 
      id: Math.random().toString(36).substr(2, 9), 
      label: '', 
      type: 'text' as 'text' | 'number',
      required: false 
    }
    setFormData(prev => ({
      ...prev,
      textBoxes: [...(prev.textBoxes || []), newTextBox]
    }))
  }

  const updateTextBox = (boxId: string, updates: Partial<MultiTextBox>) => {
    setFormData(prev => ({
      ...prev,
      textBoxes: prev.textBoxes?.map(box => 
        box.id === boxId ? { ...box, ...updates } : box
      )
    }))
  }

  const removeTextBox = (boxId: string) => {
    setFormData(prev => ({
      ...prev,
      textBoxes: prev.textBoxes?.filter(box => box.id !== boxId)
    }))
  }

  const addMatrixRow = () => {
    setFormData(prev => {
      const rows = prev.matrixRows || []
      return {
        ...prev,
        matrixRows: [
          ...rows,
          { id: generateId(), label: `Linha ${rows.length + 1}` }
        ]
      }
    })
  }

  const updateMatrixRow = (rowId: string, label: string) => {
    setFormData(prev => ({
      ...prev,
      matrixRows: prev.matrixRows?.map(row =>
        row.id === rowId ? { ...row, label } : row
      )
    }))
  }

  const removeMatrixRow = (rowId: string) => {
    setFormData(prev => ({
      ...prev,
      matrixRows: (prev.matrixRows || []).filter(row => row.id !== rowId)
    }))
  }

  const addMatrixColumn = () => {
    setFormData(prev => {
      const columns = prev.matrixColumns || []
      const nextIndex = columns.length + 1
      return {
        ...prev,
        matrixColumns: [
          ...columns,
          { id: generateId(), label: `Opção ${nextIndex}`, value: String(nextIndex) }
        ]
      }
    })
  }

  const updateMatrixColumn = (columnId: string, updates: Partial<MatrixColumn>) => {
    setFormData(prev => ({
      ...prev,
      matrixColumns: prev.matrixColumns?.map(column =>
        column.id === columnId ? { ...column, ...updates } : column
      )
    }))
  }

  const removeMatrixColumn = (columnId: string) => {
    setFormData(prev => ({
      ...prev,
      matrixColumns: (prev.matrixColumns || []).filter(column => column.id !== columnId)
    }))
  }

  const handleTypeChange = (nextType: QuestionType) => {
    setFormData((current) => convertQuestionType(current, nextType, generateId))
  }

  const questionTypeInfo = questionTypes.find(t => t.type === formData.type)

  return (
    <>
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-[70] p-4"
    >
      <div 
        className="bg-white rounded-xl max-w-5xl w-full max-h-[95vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              {questionTypeInfo && (
                <div className="bg-blue-100 p-2 rounded-lg">
                  <questionTypeInfo.icon size={24} className="text-blue-600" />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {mode === 'create' ? 'Nova Pergunta' : 'Editar Pergunta'}
                </h2>
                <p className="text-gray-500">{questionTypeInfo?.label}</p>
                {questionTypeInfo?.description && (
                  <p className="mt-1 text-xs text-gray-400">{questionTypeInfo.description}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="space-y-6">
              {/* TÃƒÆ’Ã‚Â­tulo e descriÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o com Rich Text Editor */}
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título da Pergunta *
                  </label>
                  <RichTextEditor
                    value={formData.title}
                    onChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
                    placeholder="Digite o título da pergunta"
                    height="100px"
                  />
                </div>

                <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                  <label className="mb-1 block text-sm font-semibold text-indigo-950">
                    Variável {mode === 'create' ? '*' : '(pendente em pesquisas antigas)'}
                  </label>
                  <input
                    type="text"
                    value={formData.variable || ''}
                    onChange={(event) => setFormData((previous) => ({ ...previous, variable: event.target.value }))}
                    placeholder="Ex.: PAX_Conexao ou IQS_4"
                    className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm uppercase focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="mt-2 text-xs text-indigo-800">
                    Identificador curto administrativo. Não é exibido ao respondente; será usado em condições, desvios e exportação. Use letras, números, _ ou -, sem espaços.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição (opcional)
                  </label>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder="Adicione instrucoes ou contexto para a pergunta"
                    height="120px"
                  />
                </div>

                <div className="rounded-xl border border-sky-100 bg-sky-50 p-4">
                  <div className="mb-4 flex flex-col gap-1">
                    <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-sky-800">
                      Configuração de imagem
                    </h4>
                    <p className="text-sm text-sky-700">
                      Este controle fica logo abaixo da descrição para facilitar a edição da pergunta dentro da pesquisa.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Upload da imagem da pergunta
                      </label>
                      {formData.imageUrl ? (
                        <div className="rounded-xl border border-sky-200 bg-white p-3">
                          <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <img
                              src={formData.imageUrl}
                              alt="Imagem da pergunta"
                              className="max-h-48 w-full rounded-lg object-contain"
                            />
                            <button
                              type="button"
                              onClick={handleRemoveImage}
                              className="absolute right-2 top-2 rounded-full bg-red-600 p-1 text-white transition-colors hover:bg-red-700"
                              title="Remover imagem"
                            >
                              <X size={16} />
                            </button>
                          </div>
                          <p className="mt-2 break-all text-xs text-sky-700">
                            Arquivo salvo em: {formData.imageUrl}
                          </p>
                        </div>
                      ) : (
                        <label className="flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-white px-4 text-center transition-colors hover:border-blue-500">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            disabled={uploadingImage}
                          />
                          {uploadingImage ? (
                            <>
                              <Loader2 size={28} className="mb-2 animate-spin text-blue-600" />
                              <span className="text-sm font-medium text-blue-700">Enviando imagem...</span>
                            </>
                          ) : (
                            <>
                              <Upload size={28} className="mb-2 text-gray-400" />
                              <span className="text-sm font-medium text-gray-700">Clique para fazer upload</span>
                              <span className="mt-1 text-xs text-gray-500">JPG, PNG, GIF ou SVG com ate 5MB</span>
                            </>
                          )}
                        </label>
                      )}
                      <p className="mt-2 text-xs text-sky-700">
                        Envie a imagem e escolha ao lado como ela deve aparecer para o respondente.
                      </p>
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Tamanho da imagem
                      </label>
                      <select
                        value={formData.imageDisplaySize || 'original'}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          imageDisplaySize: e.target.value as Question['imageDisplaySize']
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="small">Pequeno</option>
                        <option value="medium">Médio</option>
                        <option value="large">Grande</option>
                        <option value="original">Original</option>
                      </select>
                      <div className="mt-3 rounded-lg border border-sky-200 bg-white px-3 py-2 text-xs text-sky-800">
                        Tamanho selecionado: <strong>{getImageDisplayHint(formData.imageDisplaySize)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ConfiguraÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes gerais */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo da Pergunta
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleTypeChange(e.target.value as QuestionType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {visibleQuestionTypes.map((item) => (
                      <option key={item.type} value={item.type}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  {mode === 'edit' && (
                    <p className="mt-2 text-xs text-amber-700">
                      Ao alterar o tipo, configurações incompativeis da pergunta serao ajustadas automaticamente.
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.required}
                      onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Pergunta obrigatória</span>
                  </label>
                </div>

                {/* Seletor de pÃƒÆ’Ã‚Â¡gina */}
                {pages.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Página
                    </label>
                    <select
                      value={formData.pageId || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, pageId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selecione uma página...</option>
                      {pages.map(page => (
                        <option key={page.id} value={page.id}>
                          {stripHtml(page.title) || 'Página sem título'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

              </div>

              {/* ConfiguraÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes especÃƒÆ’Ã‚Â­ficas por tipo */}
              {(['multiple', 'single', 'checkbox', 'dropdown', 'ranking', 'classification', 'bestworst'].includes(formData.type)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opções de Resposta
                  </label>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={sortOptionsAlphabetically}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Ordenar alfabeticamente
                    </button>
                    <span className="text-xs text-gray-400">
                      Use as setas para mover opções sem reescrever a lista.
                    </span>
                  </div>
                  <div className="space-y-3">
                    {formData.options?.map((option, index) => (
                      <div key={option.id} className="flex min-w-0 items-start gap-2">
                        <span className="mt-2 w-8 shrink-0 text-sm text-gray-500">{index + 1}.</span>
                        <div className="mt-2 flex shrink-0 flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => moveOption(option.id, 'up')}
                            className="rounded border border-gray-200 p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                            title="Mover para cima"
                          >
                            <ArrowUp size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveOption(option.id, 'down')}
                            className="rounded border border-gray-200 p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                            title="Mover para baixo"
                          >
                            <ArrowDown size={14} />
                          </button>
                        </div>
                        <div className="min-w-0 flex-1">
                          <RichTextEditor
                            value={option.text}
                            onChange={(value) => updateOption(option.id, value)}
                            placeholder={`Opção ${index + 1}`}
                            className="min-w-0"
                            height="80px"
                          />
                          {canLimitSelections && (
                            <label className="mt-2 flex items-center gap-2 text-xs font-medium text-gray-700">
                              <input
                                type="checkbox"
                                checked={option.isExclusive || false}
                                onChange={(e) => updateOptionExclusive(option.id, e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span>Opção exclusiva</span>
                            </label>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openItemConditionEditor('option', option) }}
                          className={`mt-2 shrink-0 rounded-lg border px-2 py-1 text-xs font-medium ${
                            option.displayConditions?.length
                              ? 'border-purple-200 bg-purple-50 text-purple-700'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                          title="Condicoes desta opção"
                        >
                          Condicoes{option.displayConditions?.length ? ` (${option.displayConditions.length})` : ''}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeOption(option.id)}
                          className="mt-2 shrink-0 p-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addOption}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                    >
                      <Plus size={16} />
                      <span>Adicionar opção</span>
                    </button>
                  </div>

                  <div className="mt-4 space-y-2">
                    {canLimitSelections && (
                      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-blue-900">Limite de seleções</h4>
                          <p className="mt-1 text-xs text-blue-800">
                            Defina quantas opções o respondente pode marcar nesta pergunta.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">
                              Mínimo de escolhas permitidas
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={formData.options?.length || undefined}
                              value={formData.validation?.minSelections ?? ''}
                              onChange={(e) => updateSelectionLimit('minSelections', e.target.value)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Sem mínimo"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">
                              Máximo de escolhas permitidas
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={formData.options?.length || undefined}
                              value={formData.validation?.maxSelections ?? ''}
                              onChange={(e) => updateSelectionLimit('maxSelections', e.target.value)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Sem máximo"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.allowOther || false}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          allowOther: e.target.checked,
                          otherLabel: e.target.checked ? (prev.otherLabel || 'Outro') : prev.otherLabel,
                          otherPlaceholder: e.target.checked ? (prev.otherPlaceholder || 'Digite sua resposta') : prev.otherPlaceholder
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Permitir opção "Outro"</span>
                    </label>
                    {(['multiple', 'checkbox'].includes(formData.type)) && (
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.trackSelectionOrder || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, trackSelectionOrder: e.target.checked }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Registrar ordem em que as respostas forem clicadas</span>
                      </label>
                    )}
                    {formData.type === 'classification' && (
                      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
                        <strong>Resposta ordenada:</strong> na aplicacao, cada clique marca a posicao (1, 2, 3...).
                        Diferente do Ranking, nao e obrigatorio ordenar todos os itens — apenas os selecionados recebem posicao.
                        Use no pipe <code>[Q1:1]</code> para a 1a resposta, <code>[Q1:2]</code> para a 2a, etc.
                      </div>
                    )}
                    {formData.allowOther && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-gray-900">Configuração da opção Outro</h4>
                          <p className="mt-1 text-xs text-gray-600">
                            Personalize o texto exibido e a ajuda do campo complementar.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">
                              Nome exibido para o respondente
                            </label>
                            <input
                              type="text"
                              value={formData.otherLabel || 'Outro'}
                              onChange={(e) => setFormData(prev => ({ ...prev, otherLabel: e.target.value }))}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Outro"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">
                              Texto de ajuda do campo
                            </label>
                            <input
                              type="text"
                              value={formData.otherPlaceholder || 'Digite sua resposta'}
                              onChange={(e) => setFormData(prev => ({ ...prev, otherPlaceholder: e.target.value }))}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Digite sua resposta"
                            />
                          </div>
                        </div>
                        <label className="mt-3 flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.requireOtherText !== false}
                            onChange={(e) => setFormData(prev => ({ ...prev, requireOtherText: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Exigir preenchimento do campo Outro quando selecionado</span>
                        </label>
                      </div>
                    )}
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.randomizeOptions || false}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          randomizeOptions: e.target.checked,
                          randomizeFixedLastCount: e.target.checked ? (prev.randomizeFixedLastCount || 0) : 0
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Randomizar ordem das opções</span>
                    </label>
                    {formData.randomizeOptions && (
                      <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-amber-900">Randomização parcial</h4>
                          <p className="mt-1 text-xs text-amber-800">
                            Informe quantas ultimas opções devem permanecer fixas no final da lista.
                          </p>
                        </div>
                        <div className="max-w-xs">
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            Ultimas opções fixas
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={formData.options?.length || 0}
                            value={formData.randomizeFixedLastCount ?? 0}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              randomizeFixedLastCount: Math.min(
                                prev.options?.length || 0,
                                Math.max(0, Math.floor(Number(e.target.value || 0)))
                              )
                            }))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Configurações para matriz */}
              {(['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(formData.type)) && (
                <div className="space-y-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.randomizeOptions || false}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          randomizeOptions: e.target.checked,
                          randomizeFixedLastCount: e.target.checked ? (prev.randomizeFixedLastCount || 0) : 0
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-800">Randomizar ordem das linhas (e colunas/opcoes quando houver)</span>
                    </label>
                    {formData.randomizeOptions && (
                      <div className="mt-3 max-w-xs">
                        <label className="mb-1 block text-xs font-medium text-gray-700">
                          Ultimas linhas/opcoes fixas (nao entram no sorteio)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={Math.max((formData.matrixRows || []).length, (formData.matrixColumns || []).length, (formData.options || []).length)}
                          value={formData.randomizeFixedLastCount ?? 0}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            randomizeFixedLastCount: Math.max(0, Math.floor(Number(e.target.value || 0)))
                          }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                  </div>
                  {formData.type === 'slider_matrix' && (
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <input type="checkbox" checked={formData.sortMatrixRowsAlphabetically || false} onChange={(event) => setFormData((prev) => ({ ...prev, sortMatrixRowsAlphabetically: event.target.checked }))} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      Organizar itens em ordem alfabética na aplicação
                    </label>
                  )}
                  <div>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Linhas da matriz</h3>
                        <p className="mt-1 text-xs text-gray-600">
                          Cadastre os itens que serao avaliados pelo respondente.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addMatrixRow}
                        className="flex shrink-0 items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                      >
                        <Plus size={16} />
                        <span>Adicionar linha</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(formData.matrixRows || []).map((row, index) => (
                        <div key={row.id} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3">
                          <span className="w-8 shrink-0 text-sm text-gray-500">{index + 1}.</span>
                          <input
                            type="text"
                            value={row.label}
                            onChange={(e) => updateMatrixRow(row.id, e.target.value)}
                            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="Texto da linha"
                          />
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openItemConditionEditor('matrixRow', row) }}
                            className={`shrink-0 rounded-lg border px-2 py-1 text-xs font-medium ${
                              row.displayConditions?.length
                                ? 'border-purple-200 bg-purple-50 text-purple-700'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Condicoes desta linha"
                          >
                            Condicoes{row.displayConditions?.length ? ` (${row.displayConditions.length})` : ''}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeMatrixRow(row.id)}
                            className="shrink-0 p-1 text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:text-gray-300"
                            disabled={(formData.matrixRows || []).length <= 1}
                            title="Remover linha"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(['matrix', 'dropdown_matrix'].includes(formData.type)) && (
                  <div>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Colunas / opções da matriz</h3>
                        <p className="mt-1 text-xs text-gray-600">
                          Defina as respostas exibidas em cada linha da matriz.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addMatrixColumn}
                        className="flex shrink-0 items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                      >
                        <Plus size={16} />
                        <span>Adicionar coluna</span>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(formData.matrixColumns || []).map((column, index) => (
                        <div key={column.id} className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-white p-3 md:grid-cols-[2rem_1fr_10rem_auto_auto] md:items-center">
                          <span className="text-sm text-gray-500">{index + 1}.</span>
                          <input
                            type="text"
                            value={column.label}
                            onChange={(e) => updateMatrixColumn(column.id, { label: e.target.value })}
                            className="min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="Rotulo da coluna"
                          />
                          <input
                            type="text"
                            value={String(column.value)}
                            onChange={(e) => updateMatrixColumn(column.id, { value: e.target.value })}
                            className="min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            placeholder="Valor"
                          />
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openItemConditionEditor('matrixColumn', column) }}
                            className={`justify-self-start rounded-lg border px-2 py-1 text-xs font-medium md:justify-self-end ${
                              column.displayConditions?.length
                                ? 'border-purple-200 bg-purple-50 text-purple-700'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Condicoes desta coluna"
                          >
                            Condicoes{column.displayConditions?.length ? ` (${column.displayConditions.length})` : ''}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeMatrixColumn(column.id)}
                            className="justify-self-start p-1 text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:text-gray-300 md:justify-self-end"
                            disabled={(formData.matrixColumns || []).length <= 1}
                            title="Remover coluna"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
              )}

              {/* Configurações para rating/slider — pendencia Fase4: rotulos customizados */}
              {(['rating', 'slider', 'rating_matrix', 'slider_matrix'].includes(formData.type)) && (
                <div className="space-y-4 rounded-lg border border-orange-200 bg-orange-50 p-4" data-testid="slider-labels-section">
                  <div>
                    <h4 className="text-sm font-semibold text-orange-900">Rotulos da escala (exibidos na aplicacao)</h4>
                    <p className="mt-1 text-xs text-orange-800">
                      Os textos abaixo aparecem nas pontas da barra/escala no Testar Questionario. Nao use apenas o valor numerico.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor Minimo
                    </label>
                    <input
                      type="number"
                      value={formData.minValue ?? 0}
                      onChange={(e) => setFormData(prev => ({ ...prev, minValue: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor Maximo
                    </label>
                    <input
                      type="number"
                      value={formData.maxValue ?? 5}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxValue: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="question-min-label">
                      Rotulo minimo (texto exibido na aplicacao)
                    </label>
                    <RichTextEditor
                      value={formData.minLabel ?? ''}
                      onChange={(value) => setFormData(prev => ({ ...prev, minLabel: value }))}
                      placeholder="Ex: Discordo totalmente"
                      height="90px"
                    />
                    <p className="mt-1 text-xs text-gray-600">Este texto substitui o rotulo padrao na barra deslizante / escala.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="question-max-label">
                      Rotulo maximo (texto exibido na aplicacao)
                    </label>
                    <RichTextEditor
                      value={formData.maxLabel ?? ''}
                      onChange={(value) => setFormData(prev => ({ ...prev, maxLabel: value }))}
                      placeholder="Ex: Concordo totalmente"
                      height="90px"
                    />
                    <p className="mt-1 text-xs text-gray-600">Salve a pergunta e a pesquisa para o rotulo aparecer na aplicacao.</p>
                  </div>
                  <div className="col-span-2 rounded-lg border border-dashed border-orange-300 bg-white px-3 py-2 text-sm text-gray-700">
                    <span className="font-medium text-orange-900">Pre-visualizacao: </span>
                    <span>{stripHtml(formData.minLabel || '').trim() || String(formData.minValue ?? 0)}</span>
                    <span className="mx-2 text-gray-400">———</span>
                    <span>{stripHtml(formData.maxLabel || '').trim() || String(formData.maxValue ?? 5)}</span>
                  </div>
                  {(['rating', 'rating_matrix'].includes(formData.type)) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Figura da avaliacao
                        </label>
                        <select
                          value={formData.ratingIcon || 'star'}
                          onChange={(e) => setFormData(prev => ({ ...prev, ratingIcon: e.target.value as Question['ratingIcon'] }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          <option value="star">Estrela</option>
                          <option value="heart">Coracao</option>
                          <option value="thumb">Joinha</option>
                          <option value="smile">Smile</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cor da figura
                        </label>
                        <input
                          type="color"
                          value={formData.ratingColor || '#facc15'}
                          onChange={(e) => setFormData(prev => ({ ...prev, ratingColor: e.target.value }))}
                          className="h-10 w-full rounded-lg border border-gray-300 bg-white px-2 py-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tamanho da figura
                        </label>
                        <select
                          value={formData.ratingSize || 'medium'}
                          onChange={(e) => setFormData(prev => ({ ...prev, ratingSize: e.target.value as Question['ratingSize'] }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          <option value="small">Pequeno</option>
                          <option value="medium">Medio</option>
                          <option value="large">Grande</option>
                        </select>
                      </div>
                    </>
                  )}
                  {(['slider', 'slider_matrix'].includes(formData.type)) && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Incremento
                      </label>
                      <input
                        type="number"
                        value={formData.stepValue || 1}
                        onChange={(e) => setFormData(prev => ({ ...prev, stepValue: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        min="1"
                      />
                    </div>
                  )}
                  </div>
                </div>
              )}

              {/* Multiplos campos — pendencias Fase4: pipe por campo + soma = meta */}
              {formData.type === 'multitext' && (
                <div className="space-y-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="multitext-section">
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-900">Multiplos Campos</h4>
                    <p className="mt-1 text-xs text-emerald-800">
                      Use pipe no rotulo de cada campo (ex.: mercado da Q10 no campo 1, mercado da Q11 no campo 2). Ative a validacao de soma para bloquear avancar se a meta nao for atingida.
                    </p>
                  </div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campos de Texto
                  </label>
                  <div className="space-y-3">
                    {formData.textBoxes?.map((box, index) => (
                      <div key={box.id} className="min-w-0 space-y-2 rounded-lg border border-gray-200 bg-white p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-gray-500 w-8">{index + 1}.</span>
                          <input
                            type="text"
                            value={box.label}
                            onChange={(e) => updateTextBox(box.id, { label: e.target.value })}
                            className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Nome do campo (aceita pipe ex: Gasto em [Q10])"
                            data-testid={`multitext-label-${index}`}
                          />
                          <select
                            value={box.type}
                            onChange={(e) => updateTextBox(box.id, { type: e.target.value as 'text' | 'number' })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="text">Texto</option>
                            <option value="number">Número</option>
                          </select>
                          <label className="flex items-center space-x-1">
                            <input
                              type="checkbox"
                              checked={box.required || false}
                              onChange={(e) => updateTextBox(box.id, { required: e.target.checked })}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs text-gray-600">Obrig.</span>
                          </label>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              openItemConditionEditor('textBox', box)
                            }}
                            className={`rounded-lg border px-2 py-1 text-xs font-medium ${
                              box.displayConditions?.length
                                ? 'border-purple-200 bg-purple-50 text-purple-700'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Condicoes deste campo"
                          >
                            Condicoes{box.displayConditions?.length ? ` (${box.displayConditions.length})` : ''}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeTextBox(box.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="flex min-w-0 flex-wrap items-center gap-2 overflow-hidden rounded-lg border border-emerald-100 bg-emerald-50/80 p-2 pl-8" data-testid={`multitext-pipe-${index}`}>
                          <span className="text-xs font-medium text-emerald-900">Pipe no rotulo deste campo:</span>
                          <select
                            value={getPipeSourceIdForTextBox(box)}
                            onChange={(e) => setPipeSourceByBoxId((previous) => ({
                              ...previous,
                              [box.id]: e.target.value
                            }))}
                            className="min-w-0 max-w-full flex-1 truncate rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs md:max-w-[28rem]"
                          >
                            <option value="">Pergunta origem...</option>
                            {availableSourceQuestions.map((item) => (
                              <option key={item.id} value={item.id}>
                                {getQuestionSelectorLabel(item)}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => insertPipeTokenIntoTextBox(box.id, getPipeSourceIdForTextBox(box))}
                            className="rounded-lg border border-blue-200 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                          >
                            Inserir resposta
                          </button>
                          <button
                            type="button"
                            onClick={() => insertPipeTokenIntoTextBox(box.id, getPipeSourceIdForTextBox(box), 1)}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Inserir 1a
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addTextBox}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                    >
                      <Plus size={16} />
                      <span>Adicionar campo</span>
                    </button>
                  </div>

                  {/* Validacao de soma = meta */}
                  <div className="mt-2 p-4 bg-white rounded-lg border border-emerald-200" data-testid="sum-validation-section">
                    <label className="flex items-center space-x-2 mb-3">
                      <input
                        type="checkbox"
                        checked={formData.sumValidation?.enabled || false}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          sumValidation: {
                            ...prev.sumValidation,
                            enabled: e.target.checked,
                            targetSum: prev.sumValidation?.targetSum || 100,
                            errorMessage: prev.sumValidation?.errorMessage || 'A soma deve ser igual ao valor esperado'
                          }
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Validar soma dos campos (meta obrigatoria)</span>
                    </label>

                    {formData.sumValidation?.enabled && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Valor esperado da soma
                          </label>
                          <input
                            type="number"
                            value={formData.sumValidation.targetSum}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              sumValidation: {
                                ...prev.sumValidation!,
                                targetSum: parseInt(e.target.value)
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Mensagem de erro
                          </label>
                          <input
                            type="text"
                            value={formData.sumValidation.errorMessage}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              sumValidation: {
                                ...prev.sumValidation!,
                                errorMessage: e.target.value
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ConfiguraÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes para data/hora */}
              {formData.type === 'datetime' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Formato
                  </label>
                  <select
                    value={formData.dateFormat || 'date'}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateFormat: e.target.value as 'date' | 'time' | 'datetime' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="date">Apenas Data</option>
                    <option value="time">Apenas Hora</option>
                    <option value="datetime">Data e Hora</option>
                  </select>
                </div>
              )}

              {/* ValidaÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes para campos de texto/nÃƒÆ’Ã‚ºmero */}
              <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="pipe-section">
                <div>
                  <h4 className="text-sm font-semibold text-blue-900">Transferencia de Respostas / Pipe de texto</h4>
                </div>

                <div className="space-y-3 rounded-lg border border-blue-100 bg-white p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Pipe de texto no título e descrição</p>
                      <p className="text-xs text-gray-500">
                        Use codigos como <code>[Q3]</code> ou <code>[Q3:1]</code> (1a resposta). O codigo Q e sequencial nesta pesquisa.
                      </p>
                    </div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={!!(formData.answerPiping || formData.textTransfer)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            upsertAnswerPiping({
                              titleTemplate: formData.answerPiping?.titleTemplate || formData.title,
                              descriptionTemplate: formData.answerPiping?.descriptionTemplate || formData.description || ''
                            })
                          } else {
                            disableAnswerPiping()
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Ativar</span>
                    </label>
                  </div>

                  {!!(formData.answerPiping || formData.textTransfer) && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-end">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            Pergunta de origem para inserir token
                          </label>
                          <select
                            value={selectedPipeSourceId}
                            onChange={(e) => {
                              const sourceQuestionId = e.target.value
                              setPipeSourceId(sourceQuestionId)
                            }}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Selecione...</option>
                            {availableSourceQuestions.map((item) => (
                              <option key={item.id} value={item.id}>
                                {getQuestionSelectorLabel(item)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => insertResponseToken('titleTemplate', selectedPipeSourceId)}
                          className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                        >
                          Inserir no título
                        </button>
                        <button
                          type="button"
                          onClick={() => insertResponseToken('descriptionTemplate', selectedPipeSourceId)}
                          className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                        >
                          Inserir na descrição
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Para perguntas multiplas/ordenadas, &quot;Inserir no titulo&quot; usa automaticamente a <strong>1a resposta</strong> (<code>[Qn:1]</code>).
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => insertResponseToken('titleTemplate', selectedPipeSourceId, 1)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Inserir 1a resposta no título
                        </button>
                        <button
                          type="button"
                          onClick={() => insertResponseToken('titleTemplate', selectedPipeSourceId, 2)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Inserir 2a resposta no título
                        </button>
                        <button
                          type="button"
                          onClick={() => insertResponseToken('descriptionTemplate', selectedPipeSourceId, 1)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Inserir 1a resposta na descrição
                        </button>
                        <button
                          type="button"
                          onClick={() => insertResponseToken('titleTemplate', selectedPipeSourceId, undefined, true)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Inserir TODAS as respostas no título
                        </button>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700">
                          Template do título
                        </label>
                        <RichTextEditor
                          value={formData.answerPiping?.titleTemplate || formData.textTransfer?.titleTemplate || ''}
                          onChange={(value) => upsertAnswerPiping({ titleTemplate: value })}
                          placeholder="Ex: Qual nota voce daria para [Q1]?"
                          height="100px"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700">
                          Template da descrição
                        </label>
                        <RichTextEditor
                          value={formData.answerPiping?.descriptionTemplate || formData.textTransfer?.descriptionTemplate || ''}
                          onChange={(value) => upsertAnswerPiping({ descriptionTemplate: value })}
                          placeholder="Ex: Considere a resposta anterior antes de continuar."
                          height="100px"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3 rounded-lg border border-blue-100 bg-white p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Filtro dinamico de opções</p>
                      <p className="text-xs text-gray-500">
                        Exclua ou inclua respostas de uma ou mais perguntas anteriores. Use E para aplicar todos os filtros, ou OU para combinar opções selecionadas.
                      </p>
                    </div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={!!formData.optionTransfer}
                        disabled={!canFilterOptions}
                        onChange={(e) => {
                          if (e.target.checked) enableOptionTransfer()
                          else disableOptionTransfer()
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                      <span className={`text-sm ${canFilterOptions ? 'text-gray-700' : 'text-gray-400'}`}>Ativar</span>
                    </label>
                  </div>

                  {!canFilterOptions && (
                    <p className="text-xs text-amber-700">
                      O filtro de opções so aparece em perguntas de seleção.
                    </p>
                  )}

                  {formData.optionTransfer && canFilterOptions && (
                    <div className="space-y-3">
                      {getOptionTransferRules().length > 1 && (
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            Como combinar filtros
                          </label>
                          <select
                            value={formData.optionTransfer.logic || 'AND'}
                            onChange={(e) => setFormData((previous) => ({
                              ...previous,
                              optionTransfer: previous.optionTransfer
                                ? { ...previous.optionTransfer, logic: e.target.value as 'AND' | 'OR' }
                                : previous.optionTransfer
                            }))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="AND">E - aplicar todos os filtros</option>
                            <option value="OR">OU - combinar respostas das origens</option>
                          </select>
                        </div>
                      )}

                      {getOptionTransferRules().map((rule, index) => (
                        <div key={`${rule.sourceQuestionId}_${index}`} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-100 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">
                              Pergunta de origem {index + 1}
                            </label>
                            <select
                              value={rule.sourceQuestionId}
                              onChange={(e) => updateOptionTransferRule(index, { sourceQuestionId: e.target.value })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Selecione...</option>
                              {availableOptionTransferSources.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {getQuestionSelectorLabel(item)}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">
                              Modo de transferencia
                            </label>
                            <select
                              value={rule.mode}
                              onChange={(e) => updateOptionTransferRule(index, { mode: e.target.value as OptionTransferConfig['mode'] })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="exclude_selected">Excluir opções já selecionadas</option>
                              <option value="include_selected">Exibir apenas opções selecionadas</option>
                            </select>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeOptionTransferRule(index)}
                            className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                          >
                            Remover
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addOptionTransferRule}
                        className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                      >
                        Adicionar origem
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {(['text', 'opentext', 'email', 'number'].includes(formData.type)) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Validações</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {formData.type !== 'number' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Comprimento Mínimo
                          </label>
                          <input
                            type="number"
                            value={formData.validation?.minLength || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              validation: {
                                ...prev.validation,
                                minLength: e.target.value ? parseInt(e.target.value) : undefined
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Comprimento Máximo
                          </label>
                          <input
                            type="number"
                            value={formData.validation?.maxLength || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              validation: {
                                ...prev.validation,
                                maxLength: e.target.value ? parseInt(e.target.value) : undefined
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </>
                    )}

                    {formData.type === 'number' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Valor Mínimo
                          </label>
                          <input
                            type="number"
                            value={formData.validation?.minValue || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              validation: {
                                ...prev.validation,
                                minValue: e.target.value ? parseFloat(e.target.value) : undefined
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Valor Máximo
                          </label>
                          <input
                            type="number"
                            value={formData.validation?.maxValue || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              validation: {
                                ...prev.validation,
                                maxValue: e.target.value ? parseFloat(e.target.value) : undefined
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 rounded-lg border border-blue-100 bg-blue-50 p-4">
              <label className="flex items-start gap-3 text-sm text-blue-900">
                <input
                  type="checkbox"
                  checked={saveToQuestionBank}
                  onChange={(event) => setSaveToQuestionBank(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  Salvar esta pergunta tambem no banco de perguntas para reutilizar em outras pesquisas.
                </span>
              </label>
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveAndClose}
                disabled={uploadingImage}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {uploadingImage ? 'Enviando imagem...' : mode === 'create' ? 'Criar Pergunta' : 'Salvar Pergunta'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    {itemConditionTarget && (
      <ConditionEditor
        question={formData}
        allQuestions={allQuestions}
        initialConditions={itemConditionTarget.conditions}
        initialLogic={itemConditionTarget.logic}
        title="Condicoes do item"
        description={`Este item sera exibido quando: ${itemConditionTarget.label}`}
        excludeQuestionId={formData.id}
        onSave={saveItemConditions}
        onClose={() => setItemConditionTarget(null)}
      />
    )}
    </>
  )
}

// Editor de condiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes de exibiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o
const ConditionEditor: React.FC<{
  question: Question
  allQuestions: Question[]
  onSave: (conditions: DisplayCondition[], logic: 'AND' | 'OR') => void
  onClose: () => void
  initialConditions?: DisplayCondition[]
  initialLogic?: 'AND' | 'OR'
  title?: string
  description?: string
  excludeQuestionId?: string
}> = ({
  question,
  allQuestions,
  onSave,
  onClose,
  initialConditions,
  initialLogic,
  title,
  description,
  excludeQuestionId,
}) => {
  const [conditions, setConditions] = useState<DisplayCondition[]>(initialConditions || question.displayConditions || [])
  const defaultLogic = initialLogic || question.displayLogic || 'AND'

  const generateId = () => Math.random().toString(36).substr(2, 9)

  const addCondition = () => {
    const newCondition: DisplayCondition = {
      id: generateId(),
      questionId: '',
      operator: 'equals',
      value: '',
      logicalOperator: conditions.length > 0 ? 'AND' : undefined
    }
    setConditions([...conditions, newCondition])
  }

  const updateCondition = (id: string, updates: Partial<DisplayCondition>) => {
    setConditions(conditions.map(c => c.id === id ? { ...c, ...updates } : c))
  }

  const removeCondition = (id: string) => {
    setConditions(conditions.filter(c => c.id !== id))
  }

  const handleSave = () => {
    // Validar se todas as condiÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Âµes tÃƒÆ’Ã‚ªm pergunta selecionada
    const invalidConditions = conditions.filter(c => !c.questionId)
    if (invalidConditions.length > 0) {
      toast.error('Todas as condicoes devem ter uma pergunta selecionada')
      return
    }

    const missingAnswerPath = conditions.some((condition) => {
      if (condition.operator === 'is_answered' || condition.operator === 'not_answered') return false
      const sourceQuestion = allQuestions.find((item) => item.id === condition.questionId)
      return getQuestionAnswerPathOptions(sourceQuestion).length > 0 && !condition.answerPath
    })
    if (missingAnswerPath) {
      toast.error('Selecione o campo/item da pergunta composta antes de salvar a condicao.')
      return
    }

    // Normaliza os conectores: a primeira condicao nao tem conector; as
    // seguintes gravam o E/OU escolhido para combinar com a anterior.
    const normalizedConditions = conditions.map((condition, conditionIndex) => ({
      ...condition,
      logicalOperator: conditionIndex === 0 ? undefined : (condition.logicalOperator || defaultLogic)
    }))

    onSave(normalizedConditions, defaultLogic)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const modal = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 2147483000 }}
      onClick={handleBackdropClick}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div 
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{title || 'Condicoes de Exibicao'}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {description || 'Esta pergunta sera exibida quando:'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          {/* O conector E/OU de cada condicao e editavel entre as condicoes */}


          {/* Lista de condições */}
          <div className="space-y-4">
            {conditions.map((condition, index) => (
              <div key={condition.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Condicao {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeCondition(condition.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Selecionar pergunta */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pergunta
                    </label>
                    <select
                      value={condition.questionId}
                      onChange={(e) => updateCondition(condition.id, {
                        questionId: e.target.value,
                        answerPath: undefined,
                        optionId: undefined,
                        value: undefined
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">Selecione...</option>
                      {allQuestions
                        .filter(q => q.id !== (excludeQuestionId || question.id))
                        .map(q => (
                          <option key={q.id} value={q.id}>
                            {getQuestionReferenceLabel(q)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {getQuestionAnswerPathOptions(allQuestions.find((item) => item.id === condition.questionId)).length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Campo/item
                      </label>
                      <select
                        value={condition.answerPath || ''}
                        onChange={(e) => updateCondition(condition.id, {
                          answerPath: e.target.value || undefined,
                          optionId: undefined,
                          value: undefined
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">Resposta completa</option>
                        {getQuestionAnswerPathOptions(allQuestions.find((item) => item.id === condition.questionId)).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Operador */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Operador
                    </label>
                    <select
                      value={condition.operator}
                      onChange={(e) => updateCondition(condition.id, { operator: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="equals">Igual a</option>
                      <option value="not_equals">Diferente de</option>
                      <option value="contains">Contem</option>
                      <option value="greater_than">Maior que</option>
                      <option value="less_than">Menor que</option>
                      <option value="is_answered">Foi respondida</option>
                      <option value="not_answered">Não foi respondida</option>
                    </select>
                  </div>

                  {/* Valor ou opÃƒÆ’Ã‚Â§ÃƒÆ’Ã‚Â£o */}
                  {!['is_answered', 'not_answered'].includes(condition.operator) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Valor
                      </label>
                      {condition.questionId && getQuestionAnswerValueOptions(
                        allQuestions.find((item) => item.id === condition.questionId),
                        condition.answerPath
                      ).length > 0 ? (
                        <select
                          value={condition.optionId || condition.value || ''}
                          onChange={(e) => {
                            const selectedQuestion = allQuestions.find((item) => item.id === condition.questionId)
                            const valueOptions = getQuestionAnswerValueOptions(selectedQuestion, condition.answerPath)
                            const selectedOption = valueOptions.find((option) => option.value === e.target.value)
                            updateCondition(condition.id, selectedOption?.storeAs === 'optionId'
                              ? { optionId: e.target.value, value: undefined }
                              : { value: e.target.value, optionId: undefined }
                            )
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                          <option value="">Selecione...</option>
                          {getQuestionAnswerValueOptions(
                            allQuestions.find((item) => item.id === condition.questionId),
                            condition.answerPath
                          ).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={condition.value || ''}
                          onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                          placeholder="Digite o valor"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      )}
                    </div>
                  )}
                </div>

                {index < conditions.length - 1 && (
                  <div className="mt-3 text-center">
                    <select
                      value={conditions[index + 1].logicalOperator || defaultLogic}
                      onChange={(e) => updateCondition(conditions[index + 1].id, { logicalOperator: e.target.value as 'AND' | 'OR' })}
                      className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full border-0 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="AND">E (AND)</option>
                      <option value="OR">Ou (OR)</option>
                    </select>
                  </div>
                )}
              </div>
            ))}

            {conditions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-4">Nenhuma condicao definida</p>
                <p className="text-sm">Esta pergunta sera sempre exibida</p>
              </div>
            )}

            <button
              type="button"
              onClick={addCondition}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus size={20} />
              <span>Adicionar Condicao</span>
            </button>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Salvar Condicoes
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}

// Editor de desvios de página
const BranchEditor: React.FC<{
  question: Question
  questions: Question[]
  pages: SurveyPage[]
  onSave: (branches: ConditionalBranch[]) => void
  onClose: () => void
}> = ({ question, questions, pages, onSave, onClose }) => {
  const [branches, setBranches] = useState<ConditionalBranch[]>(question.conditionalBranches || [])

  const generateId = () => Math.random().toString(36).substr(2, 9)

  const addBranch = () => {
    const newBranch: ConditionalBranch = {
      id: generateId(),
      optionId: '',
      targetPageId: '',
      condition: 'equals'
    }
    setBranches([...branches, newBranch])
  }

  const updateBranch = (id: string, updates: Partial<ConditionalBranch>) => {
    setBranches(branches.map(b => b.id === id ? { ...b, ...updates } : b))
  }

  const removeBranch = (id: string) => {
    setBranches(branches.filter(b => b.id !== id))
  }

  const handleSave = () => {
    // Validar se todos os desvios têm opção e destino (pergunta ou página) selecionados
    const invalidBranches = branches.filter(b => !b.optionId || (!b.targetPageId && !b.targetQuestionId))
    if (invalidBranches.length > 0) {
      toast.error('Todos os desvios devem ter uma opção e um destino (pergunta ou página) selecionados')
      return
    }

    if (answerPathOptions.length > 0 && branches.some((branch) => !branch.answerPath)) {
      toast.error('Selecione o campo/item da pergunta composta antes de salvar o desvio.')
      return
    }

    onSave(branches)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  // Perguntas de qualquer pagina (mesma pagina ou outra) podem ser destino do desvio.
  const branchTargetQuestionOptions = (() => {
    const options: Array<{ questionId: string; label: string }> = []
    const seen = new Set<string>()

    pages.forEach((targetPage) => {
      ;(targetPage.questionIds || []).forEach((questionId) => {
        if (seen.has(questionId)) return
        const targetQuestion = questions.find((item) => item.id === questionId)
        if (!targetQuestion) return
        seen.add(questionId)
        const pageLabel = stripHtml(targetPage.title) || 'Página sem título'
        const questionLabel = targetQuestion.variable?.trim() || stripHtml(targetQuestion.title || '') || 'Pergunta sem título'
        options.push({ questionId, label: `${pageLabel} — ${questionLabel}` })
      })
    })

    questions.forEach((targetQuestion) => {
      if (seen.has(targetQuestion.id)) return
      seen.add(targetQuestion.id)
      const questionLabel = targetQuestion.variable?.trim() || stripHtml(targetQuestion.title || '') || 'Pergunta sem título'
      options.push({ questionId: targetQuestion.id, label: questionLabel })
    })

    return options
  })()

  // Verificar se a pergunta tem opções para desvio
  const answerPathOptions = getQuestionAnswerPathOptions(question)
  const hasOptions = (question.options && question.options.length > 0) || answerPathOptions.length > 0
  const getBranchAnswerValueOptions = (answerPath?: string) =>
    getQuestionAnswerValueOptions(question, answerPath)
  const getBranchAnswerLabel = (branch: ConditionalBranch) =>
    getBranchAnswerValueOptions(branch.answerPath).find((option) => option.value === branch.optionId)?.label ||
    stripHtml(question.options?.find((option) => option.id === branch.optionId)?.text || '') ||
    branch.optionId
  
  if (!hasOptions) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4"
        onClick={handleBackdropClick}
      >
        <div 
          className="bg-white rounded-xl max-w-md w-full p-6"
          onClick={handleModalClick}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Desvios de Página</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">
              Esta pergunta não possui opções de resposta.
            </p>
            <p className="text-sm text-gray-500">
              Desvios condicionais so estao disponiveis para perguntas com opções (multipla escolha, única, dropdown, etc.)
            </p>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Desvios de Página</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure para onde o usuário sera direcionado com base na resposta
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          {/* Info sobre a pergunta */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
            <h3 className="font-medium text-blue-900 mb-2">Pergunta:</h3>
            <p className="text-blue-800">
              {question.title ? stripHtml(question.title) : 'Sem título'}
            </p>
          </div>

          {/* Lista de desvios */}
          <div className="space-y-4">
            {branches.map((branch, index) => (
              <div key={branch.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Desvio {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeBranch(branch.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {answerPathOptions.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Campo/item
                      </label>
                      <select
                        value={branch.answerPath || ''}
                        onChange={(e) => updateBranch(branch.id, {
                          answerPath: e.target.value || undefined,
                          optionId: ''
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">Resposta completa</option>
                        {answerPathOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Condicao */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Condicao
                    </label>
                    <select
                      value={branch.condition}
                      onChange={(e) => updateBranch(branch.id, { condition: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="equals">Se responder</option>
                      <option value="not_equals">Se não responder</option>
                      <option value="contains">Se contiver</option>
                      <option value="greater_than">Se for maior que</option>
                      <option value="less_than">Se for menor que</option>
                      <option value="greater_or_equal">Se for maior ou igual a</option>
                      <option value="less_or_equal">Se for menor ou igual a</option>
                    </select>
                  </div>

                  {/* Opção selecionada */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Valor
                    </label>
                    {getBranchAnswerValueOptions(branch.answerPath).length > 0 ? (
                      <select
                        value={branch.optionId}
                        onChange={(e) => updateBranch(branch.id, { optionId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">Selecione...</option>
                        {getBranchAnswerValueOptions(branch.answerPath).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={branch.optionId}
                        onChange={(e) => updateBranch(branch.id, { optionId: e.target.value })}
                        placeholder="Digite o valor"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    )}
                  </div>

                  {/* Destino do desvio (pergunta ou página) */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Ir para
                    </label>
                    <select
                      value={branch.targetQuestionId ? `question:${branch.targetQuestionId}` : (branch.targetPageId ? `page:${branch.targetPageId}` : '')}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value.startsWith('question:')) {
                          updateBranch(branch.id, { targetQuestionId: value.slice('question:'.length), targetPageId: '' })
                        } else if (value.startsWith('page:')) {
                          updateBranch(branch.id, { targetPageId: value.slice(5), targetQuestionId: undefined })
                        } else {
                          updateBranch(branch.id, { targetPageId: '', targetQuestionId: undefined })
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">Selecione...</option>
                      <optgroup label="Perguntas (mesma página ou outra)">
                        {branchTargetQuestionOptions.map((item) => (
                          <option key={item.questionId} value={`question:${item.questionId}`}>
                            {item.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Páginas">
                        {pages.map(targetPage => (
                          <option key={targetPage.id} value={`page:${targetPage.id}`}>
                            {stripHtml(targetPage.title) || 'Página sem título'}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                {/* Preview da regra */}
                {branch.optionId && (branch.targetPageId || branch.targetQuestionId) && (
                  <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
                    <p className="text-xs text-green-800">
                      <strong>Regra:</strong> {branch.condition === 'equals' ? 'Se' : branch.condition === 'not_equals' ? 'Se nao' : 'Se contiver'} resposta "{getBranchAnswerLabel(branch)}" {'->'} ir para "{branch.targetQuestionId ? `Pergunta: ${branchTargetQuestionOptions.find((item) => item.questionId === branch.targetQuestionId)?.label || branch.targetQuestionId}` : stripHtml(pages.find(p => p.id === branch.targetPageId)?.title || '')}"
                    </p>
                  </div>
                )}
              </div>
            ))}

            {branches.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-4">Nenhum desvio configurado</p>
                <p className="text-sm">O fluxo seguira a ordem padrão das páginas</p>
              </div>
            )}

            <button
              type="button"
              onClick={addBranch}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-500 hover:text-purple-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus size={20} />
              <span>Adicionar Desvio</span>
            </button>
          </div>

          {/* Info adicional */}
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h4 className="text-sm font-medium text-yellow-900 mb-2">Como funciona:</h4>
            <ul className="text-xs text-yellow-800 space-y-1">
              <li>Os desvios sao avaliados na ordem em que aparecem.</li>
              <li>O primeiro desvio que corresponder sera aplicado.</li>
              <li>Se nenhum desvio corresponder, o fluxo segue para a próxima página na ordem.</li>
              <li>Você pode criar multiplos desvios para diferentes respostas.</li>
            </ul>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Salvar Desvios
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Componente de busca no banco de perguntas
interface BankQuestion {
  _id: string
  type: string
  translations: {
    pt: {
      title: string
      description?: string
      options?: string[]
    }
    en?: {
      title?: string
      description?: string
      options?: string[]
    }
    es?: {
      title?: string
      description?: string
      options?: string[]
    }
  }
  isRequired: boolean
  category?: string
}

const QuestionBankSearch: React.FC<{
  onSelect: (question: BankQuestion) => void
  onClose: () => void
}> = ({ onSelect, onClose }) => {
  const [questions, setQuestions] = useState<BankQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadQuestions()
  }, [])

  const loadQuestions = async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.questions.list({
        sort: { createdAt: -1 }
      })
      setQuestions(response.list || [])
    } catch (error) {
      console.error('Erro ao carregar banco de perguntas:', error)
      toast.error('Erro ao carregar banco de perguntas')
    } finally {
      setLoading(false)
    }
  }

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const filteredQuestions = questions.filter(q => 
    stripHtml(q.translations?.pt?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.category?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((left, right) => (
    stripHtml(left.translations?.pt?.title || '').localeCompare(
      stripHtml(right.translations?.pt?.title || ''),
      'pt-BR',
      { sensitivity: 'base' }
    )
  ))

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const getTypeLabel = (type: string) => {
    const types = {
      'multiple_choice': 'Múltipla Escolha',
      'single_choice': 'Escolha Única',
      'multiple_select': 'Seleção Múltipla',
      'ordered_multiple': 'Ranking Ordenado',
      'dropdown': 'Lista Suspensa',
      'matrix_dropdown': 'Lista Suspensa Combinada',
      'dropdown_matrix': 'Lista Suspensa Combinada',
      'media': 'Imagem/Video',
      'star_rating': 'Avaliação Estrelas',
      'rating_matrix': 'Estrelas Combinada',
      'slider': 'Barra Deslizante',
      'slider_matrix': 'Escala Deslizante Combinada',
      'ranking': 'Ranking Ordenado',
      'classification': 'Resposta Ordenada',
      'best_worst': 'Melhor/Pior',
      'open_text': 'Texto Livre',
      'open_number': 'Número',
      'multiple_textboxes': 'Várias Caixas',
      'datetime': 'Data/Hora',
      'email': 'E-mail'
    }
    return types[type as keyof typeof types] || type
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Banco de Perguntas</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          {/* Campo de busca */}
          <div className="mb-6 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar perguntas por título ou categoria..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm ? 'Nenhuma pergunta encontrada' : 'Nenhuma pergunta no banco'}
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {filteredQuestions.map(question => (
                <div
                  key={question._id}
                  onClick={() => onSelect(question)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {getTypeLabel(question.type)}
                      </span>
                      {question.isRequired && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                          Obrigatória
                        </span>
                      )}
                      {question.category && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                          {question.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {stripHtml(question.translations?.pt?.title || 'Pergunta sem título')}
                  </h3>
                  {question.translations?.pt?.description && (
                    <p className="text-sm text-gray-600 mb-2">
                      {stripHtml(question.translations.pt.description)}
                    </p>
                  )}
                  {question.translations?.pt?.options && question.translations.pt.options.length > 0 && (
                    <div className="text-xs text-gray-500">
                      {question.translations.pt.options.length} opções disponiveis
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuestionBuilder